import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  getTenants,
  createTenantWithAdmin,
  toggleTenantStatus,
  getTenantPurgePreview,
  permanentlyDeleteTenant,
  updateTenantLogo,
  updateTenantAttDefaultAddress,
  updateTenantNitelBillingAccount,
  updateTenantSiteClassifications,
  DEFAULT_SITE_CLASSIFICATIONS,
  generatePowerBIKey,
  getTenantPowerBIKey,
  regeneratePowerBIKey,
  getSuperAdminSettings,
  updateSuperAdminSettings,
  updateTenantInvoiceEmailSettings,
  getClaudeModels,
  getTenantBillingAccounts,
  updateTenantBillingAccounts,
  getTenantGlCodes,
  updateTenantGlCodes,
} from "../services/superAdminService";
import { getAllUsers, updateUser, deleteUser, createUser } from "../services/userService";
import CreateTenantAdminModal from "../components/modals/CreateTenantAdminModal";
import AddUserToTenantModal from "../components/modals/AddUserToTenantModal";
import { formatDateYmdStable } from "../utils/dateUtils";
import {
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Search,
  Settings,
  MoreHorizontal,
  KeyRound,
  EyeOff,
  Eye,
  UserPlus,
  Ban,
  CircleSlash,
  Copy,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import BillingAccountsSettings from "../components/superAdmin/BillingAccountsSettings";
import BudgetGlCodesSettings from "../components/superAdmin/BudgetGlCodesSettings";
import BudgetEntryView from "../components/clientAdmin/BudgetEntryView";
import FootprintBaselineClientAdminView from "../components/clientAdmin/FootprintBaselineClientAdminView";
import { getTenantAiPromptStatuses } from "../services/aiPromptsService";
import { DiffView } from "../utils/textDiff";
import { normalizeBillingAccount } from "../utils/helpers";

import CustomSelect from "../components/ui/CustomSelect";
import Toast from "../components/ui/Toast";
const MAX_LOGO_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];

const DEFAULT_ATT_IMPORT_ADDRESS = {
  street_address: "",
  city: "",
  state: "",
  zip: "",
  country: "",
};

/** Fallback only; live From/Reply-To come from backend AZURE_INVOICE_SENDER_EMAIL. */
const INVOICE_EMAIL_ADDRESS_FALLBACK = "info@jeblsolutions.com";

// const formatAccessExpires = (dateString) => {
//   if (!dateString) return "—";
//   const d = new Date(dateString);
//   const day = String(d.getDate()).padStart(2, "0");
//   const month = String(d.getMonth() + 1).padStart(2, "0");
//   const year = d.getFullYear();
//   return `${day}-${month}-${year}`;
// };

const formatAccessExpires = (dateString) => {
  if (!dateString) return "—";
  return formatDateYmdStable(dateString) || "—";
};

const MS_DAY = 86400000;
const EXPIRING_WINDOW_DAYS = 30;

const parseDateValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Calendar YYYY-MM-DD for access expiry (date-only strings stay unshifted). */
const toAccessYmd = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") {
    const ymd = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (ymd) return ymd[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

const todayAccessYmd = () => {
  const n = new Date();
  return [
    n.getFullYear(),
    String(n.getMonth() + 1).padStart(2, "0"),
    String(n.getDate()).padStart(2, "0"),
  ].join("-");
};

const daysBetweenYmd = (fromYmd, toYmd) => {
  const [fy, fm, fd] = fromYmd.split("-").map(Number);
  const [ty, tm, td] = toYmd.split("-").map(Number);
  return Math.round(
    (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / MS_DAY,
  );
};

const formatDisplayDate = (value) => {
  const ymd = toAccessYmd(value);
  if (ymd) {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const parsed = parseDateValue(value);
  if (!parsed) return "—";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatRelativePast = (value) => {
  const d = parseDateValue(value);
  if (!d) return "—";
  const days = Math.floor((Date.now() - d.getTime()) / MS_DAY);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return formatDisplayDate(value);
};

const formatRelativeFuture = (value) => {
  const ymd = toAccessYmd(value);
  if (ymd) {
    const days = daysBetweenYmd(todayAccessYmd(), ymd);
    if (days < 0) {
      const ago = Math.abs(days);
      return ago === 1 ? "1 day ago" : `${ago} days ago`;
    }
    if (days === 0) return "today";
    if (days === 1) return "in 1 day";
    return `in ${days} days`;
  }
  const d = parseDateValue(value);
  if (!d) return "—";
  const days = Math.ceil((d.getTime() - Date.now()) / MS_DAY);
  if (days < 0) {
    const ago = Math.abs(days);
    return ago === 1 ? "1 day ago" : `${ago} days ago`;
  }
  if (days === 0) return "today";
  if (days === 1) return "in 1 day";
  return `in ${days} days`;
};

/** Expired = access end date is before today (calendar day). */
const isAccessExpired = (user) => {
  const ymd = toAccessYmd(user?.accessExpiresAt);
  if (!ymd) return false;
  return ymd < todayAccessYmd();
};

/** Expiring = still valid today, and ends within the next N calendar days. */
const isAccessExpiringSoon = (user, withinDays = EXPIRING_WINDOW_DAYS) => {
  const ymd = toAccessYmd(user?.accessExpiresAt);
  if (!ymd) return false;
  const days = daysBetweenYmd(todayAccessYmd(), ymd);
  return days >= 0 && days <= withinDays;
};

const StatusPill = ({ active, activeLabel = "Active", inactiveLabel = "Inactive" }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
      active
        ? "bg-[#E6F4EA] text-[#1E7E34]"
        : "bg-[#F1F3F4] text-[#5F6368]"
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        active ? "bg-[#1E7E34]" : "bg-[#5F6368]"
      }`}
    />
    {active ? activeLabel : inactiveLabel}
  </span>
);

const AccessPill = ({ kind, label }) => {
  const styles =
    kind === "expired"
      ? "bg-[#FCE8E6] text-[#D93025]"
      : kind === "expiring"
      ? "bg-[#FEF3E2] text-[#B06000]"
      : "bg-[#E6F4EA] text-[#1E7E34]";
  const dot =
    kind === "expired"
      ? "bg-[#D93025]"
      : kind === "expiring"
      ? "bg-[#B06000]"
      : "bg-[#1E7E34]";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${styles}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
};

/** Hide → 30-day wait → permanent delete schedule for a tenant row. */
const getTenantDeletionInfo = (tenant) => {
  if (!tenant || tenant.isActive !== false) {
    return { phase: "active", canPurge: false, daysLeft: null, eligibleAt: null };
  }
  const eligible =
    parseDateValue(tenant.deletionEligibleAt) ||
    new Date(Date.now() + 30 * MS_DAY);
  const daysLeft = Math.ceil((eligible.getTime() - Date.now()) / MS_DAY);
  if (daysLeft > 0) {
    return { phase: "waiting", canPurge: false, daysLeft, eligibleAt: eligible };
  }
  return { phase: "ready", canPurge: true, daysLeft: 0, eligibleAt: eligible };
};

const findDuplicateBillingAccount = (accounts) => {
  const seen = new Set();
  for (const account of accounts || []) {
    const provider = String(account?.provider ?? "").trim();
    const billingAccount = normalizeBillingAccount(account?.billing_account);
    if (!provider || !billingAccount) continue;
    const key = `${provider.toLowerCase()}\u0000${billingAccount.toLowerCase()}`;
    if (seen.has(key)) {
      return { provider, billingAccount };
    }
    seen.add(key);
  }
  return null;
};

const INPUT_CLASS =
  "w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)] disabled:bg-gray-50 disabled:cursor-not-allowed";

const LABEL_CLASS = "block text-sm font-medium text-gray-700 mb-1.5";

const FormSection = ({ title, description, children, isFirst }) => (
  <div
    className={`grid grid-cols-1 md:grid-cols-[minmax(180px,28%)_1fr] gap-4 md:gap-10 py-7 ${
      isFirst ? "" : "border-t border-gray-200"
    }`}
  >
    <div className="md:pt-0.5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
    </div>
    <div className="space-y-4 min-w-0">{children}</div>
  </div>
);

// --- Tenant settings modal: tab bar -----------------------------------------
const SETTINGS_TABS = [
  { id: "general", label: "General" },
  { id: "invoicing", label: "Invoicing" },
  { id: "ai", label: "AI automation" },
  { id: "budget-gl-codes", label: "Budget GL Codes" },
  { id: "billing-accounts", label: "Billing Accounts" },
  { id: "budget-entry", label: "Budget Entry" },
  { id: "footprint-baseline", label: "Site Footprint Baseline" },
];

/** Tabs that save themselves (no modal-level Save Changes). */
const SELF_SAVE_SETTINGS_TABS = new Set(["budget-entry", "footprint-baseline"]);

const SettingsTabBar = ({ activeTab, onChange }) => (
  <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 mb-6 -mt-2">
    {SETTINGS_TABS.map((tab) => (
      <button
        key={tab.id}
        type="button"
        onClick={() => onChange(tab.id)}
        className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
          activeTab === tab.id
            ? "border-gray-900 text-gray-900"
            : "border-transparent text-gray-500 hover:text-gray-700"
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// A textarea that always grows to fit its content — never shows an internal
// scrollbar, however long the prompt gets. The surrounding modal body scrolls instead.
const AutoGrowTextarea = ({ value, onChange, placeholder, className = "", minHeight = 90 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, [value, minHeight]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${className} resize-none overflow-hidden`}
      style={{ minHeight: `${minHeight}px` }}
    />
  );
};

const PromptStatusPill = ({ status, label }) => {
  const styles = {
    matches: "bg-[#e6f4ea] text-[#137333]",
    behind: "bg-[#fef3e2] text-[#b06000]",
    custom: "bg-[#fef3e2] text-[#b06000]",
    not_set: "bg-[#f0efeb] text-[#5c5b57]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${
        styles[status] || styles.not_set
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          status === "matches" ? "bg-[#137333]" : status === "not_set" ? "bg-[#9a9994]" : "bg-[#b06000]"
        }`}
      />
      {label || status}
    </span>
  );
};

const FeatureToggle = ({ checked, onChange, id }) => (
  <button
    type="button"
    id={id}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/30 ${
      checked ? "bg-[#34a853]" : "bg-gray-300"
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

/** Collapsible prompts row — status pills stay visible when closed. */
const FeaturePromptsRow = ({
  isOpen,
  onToggle,
  summaryPills,
  children,
}) => (
  <div className="mt-4 border-t border-gray-100 pt-3">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 text-left"
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800">
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-gray-500" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" aria-hidden="true" />
        )}
        Prompts
      </span>
      {!isOpen && summaryPills ? (
        <span className="flex flex-wrap items-center gap-2 justify-end">
          {summaryPills}
        </span>
      ) : null}
    </button>
    {isOpen && <div className="grid grid-cols-1 gap-4 mt-3">{children}</div>}
  </div>
);

/** Claude model dropdown (reuse existing Anthropic model list). */
const ClaudeModelSelect = ({
  label,
  value,
  onChange,
  models,
  loading,
  error,
  defaultLabel = "Default (env / Sonnet)",
}) => (
  <div>
    <label className={LABEL_CLASS}>{label}</label>
    <CustomSelect
      className={INPUT_CLASS}
      value={value || ""}
      onChange={onChange}
      disabled={loading}
    >
      <option value="">
        {loading ? "Loading models…" : defaultLabel}
      </option>
      {models.map((m) => (
        <option key={m.id} value={m.id}>
          {m.displayName || m.id}
        </option>
      ))}
      {value && !models.some((m) => m.id === value) && (
        <option value={value}>
          {value} (saved)
        </option>
      )}
    </CustomSelect>
    {error && (
      <p className="text-xs text-amber-700 mt-1">
        {error}. You can still keep the default or a previously saved model.
      </p>
    )}
  </div>
);

const getEditRoleDisplay = (role) => {
  if (role === "viewer") return "Read Only";
  if (role === "admin") return "Admin (full access)";
  if (!role) return "—";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const SuperAdminDashboard = ({ onNavigate }) => {
  const { refreshProfile } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [editUserModal, setEditUserModal] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [deleteUserModal, setDeleteUserModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [apiKeyModal, setApiKeyModal] = useState(null);
  const [apiKeyData, setApiKeyData] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [emailConfigModal, setEmailConfigModal] = useState(null);
  const [emailConfigForm, setEmailConfigForm] = useState({
    emailDisplayName: "",
    emailFrom: INVOICE_EMAIL_ADDRESS_FALLBACK,
    emailReplyTo: INVOICE_EMAIL_ADDRESS_FALLBACK,
    emailTo: "",
    emailCc: "",
    emailSubject: "",
    customField1Name: "",
    customField2Name: "",
    invoiceNoteAutoEnabled: false,
    invoiceMrcChangeThreshold: 5,
    invoiceClaudePrompt: "",
    monthSummaryClaudePrompt: "",
    monthSummaryAutoOnClose: false,
    invoiceGrammarClaudePrompt: "",
    monthSummaryGrammarClaudePrompt: "",
    invoiceClaudeModel: "",
    invoiceGrammarClaudeModel: "",
    monthSummaryClaudeModel: "",
    monthSummaryGrammarClaudeModel: "",
    invoiceExtractionClaudePrompt: "",
    invoiceExtractionClaudeModel: "",
  });
  const [emailConfigSaving, setEmailConfigSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [invoiceLogoDataUrl, setInvoiceLogoDataUrl] = useState(null);
  const [invoiceLogoError, setInvoiceLogoError] = useState("");
  const [invoiceAttStreet, setInvoiceAttStreet] = useState(DEFAULT_ATT_IMPORT_ADDRESS.street_address);
  const [invoiceAttCity, setInvoiceAttCity] = useState(DEFAULT_ATT_IMPORT_ADDRESS.city);
  const [invoiceAttState, setInvoiceAttState] = useState(DEFAULT_ATT_IMPORT_ADDRESS.state);
  const [invoiceAttZip, setInvoiceAttZip] = useState(DEFAULT_ATT_IMPORT_ADDRESS.zip);
  const [invoiceAttCountry, setInvoiceAttCountry] = useState(DEFAULT_ATT_IMPORT_ADDRESS.country);
  const [nitelBillingAccount, setNitelBillingAccount] = useState("");
  const [siteClassificationsForm, setSiteClassificationsForm] = useState([
    ...DEFAULT_SITE_CLASSIFICATIONS,
  ]);
  const [defaultAccessExpirationDays, setDefaultAccessExpirationDays] = useState(90);
  const [extendAccessUser, setExtendAccessUser] = useState(null);
  const [extendAccessDays, setExtendAccessDays] = useState(90);
  const [extendAccessLoading, setExtendAccessLoading] = useState(false);
  const [expandedTenantKey, setExpandedTenantKey] = useState(null);
  const invoiceLogoInputRef = useRef(null);

  // --- Tenant settings modal UI-only state (tabs + prompt disclosures) ------
  const [settingsTab, setSettingsTab] = useState("general");
  // Save action lifted out of the Budget Entry tab so it can sit beside the
  // modal's Close button.
  const [budgetFooterAction, setBudgetFooterAction] = useState(null);
  const [showMonthSummaryPrompts, setShowMonthSummaryPrompts] = useState(false);
  const [showInvoiceNotePrompts, setShowInvoiceNotePrompts] = useState(false);
  const [showExtractionPrompts, setShowExtractionPrompts] = useState(false);
  const [tenantPromptStatuses, setTenantPromptStatuses] = useState({});
  const [tenantPromptMasters, setTenantPromptMasters] = useState({});
  const [promptDiffModal, setPromptDiffModal] = useState(null);
  const [claudeModels, setClaudeModels] = useState([]);
  const [claudeModelsLoading, setClaudeModelsLoading] = useState(false);
  const [claudeModelsError, setClaudeModelsError] = useState("");
  const [billingProvidersForm, setBillingProvidersForm] = useState([]);
  const [billingAccountsForm, setBillingAccountsForm] = useState([]);
  const [billingAccountsLoading, setBillingAccountsLoading] = useState(false);
  const [billingAccountsLoaded, setBillingAccountsLoaded] = useState(false);
  const [glCodesForm, setGlCodesForm] = useState([]);
  const [glCodesLoading, setGlCodesLoading] = useState(false);
  const [glCodesLoaded, setGlCodesLoaded] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [tenantVisibilityTab, setTenantVisibilityTab] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [accessFilter, setAccessFilter] = useState("all");
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [addUserInitialTenantId, setAddUserInitialTenantId] = useState("");
  const [purgeModal, setPurgeModal] = useState(null);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgePreviewLoading, setPurgePreviewLoading] = useState(false);
  const [purgeConfirmation, setPurgeConfirmation] = useState("");
  const [purgeAcknowledged, setPurgeAcknowledged] = useState(false);
  const [purgeSubmitting, setPurgeSubmitting] = useState(false);
  const actionMenuRef = useRef(null);

  const getTenantKey = (tenant) => tenant?.tenantId || tenant?.tenantName || "";

  const closeActionMenu = () => setOpenActionMenu(null);

  const toggleActionMenu = (menu, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (openActionMenu?.id === menu.id) {
      setOpenActionMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const estimatedHeight = menu.kind === "tenant" ? 220 : 120;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < estimatedHeight && rect.top > estimatedHeight;
    setOpenActionMenu({
      ...menu,
      top: placeAbove ? rect.top - 4 : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
      placeAbove,
    });
  };

  const toggleTenantExpanded = (tenantKey) => {
    if (!tenantKey) return;
    setOpenActionMenu(null);
    setExpandedTenantKey((prev) => (prev === tenantKey ? null : tenantKey));
  };

  const isActiveUser = (u) =>
    u &&
    u.role !== "super_admin" &&
    u.isDeleted !== true &&
    u.is_deleted !== true;

  const getUsersForTenant = (tenant) =>
    users.filter(
      (u) =>
        isActiveUser(u) &&
        (u.tenantId === tenant.tenantId || u.tenantName === tenant.tenantName),
    );

  const countActiveUsersForTenant = (tenant) => getUsersForTenant(tenant).length;

  const countAdminsForTenant = (tenant) =>
    getUsersForTenant(tenant).filter((u) => u.role === "admin").length;

  const getTenantAccessSummary = (tenant) => {
    const tenantUsers = getUsersForTenant(tenant);
    const expired = tenantUsers.filter(isAccessExpired).length;
    const expiring = tenantUsers.filter(
      (u) => !isAccessExpired(u) && isAccessExpiringSoon(u),
    ).length;
    if (expired > 0) {
      return {
        kind: "expired",
        label: expired === 1 ? "1 expired" : `${expired} expired`,
        expired,
        expiring,
      };
    }
    if (expiring > 0) {
      return {
        kind: "expiring",
        label:
          expiring === 1
            ? "1 expiring in 30 days"
            : `${expiring} expiring in 30 days`,
        expired,
        expiring,
      };
    }
    return { kind: "current", label: "All current", expired: 0, expiring: 0 };
  };

  const getTenantLastActivity = (tenant) => {
    const logins = getUsersForTenant(tenant)
      .map((u) => parseDateValue(u.lastLogin))
      .filter(Boolean)
      .sort((a, b) => b.getTime() - a.getTime());
    return logins[0] || null;
  };

  const getExpiredUsersSummary = () => {
    let userCount = 0;
    const tenantKeys = new Set();
    getDisplayTenants().forEach((tenant) => {
      const summary = getTenantAccessSummary(tenant);
      if (summary.kind !== "expired") return;
      userCount += summary.expired;
      tenantKeys.add(getTenantKey(tenant));
    });
    return { userCount, tenantCount: tenantKeys.size };
  };

  const getExpiringUsersSummary = () => {
    let userCount = 0;
    const tenantKeys = new Set();
    getDisplayTenants().forEach((tenant) => {
      // Same rule as ACCESS badge / filter: expired wins; only pure-expiring tenants.
      const summary = getTenantAccessSummary(tenant);
      if (summary.kind !== "expiring") return;
      userCount += summary.expiring;
      tenantKeys.add(getTenantKey(tenant));
    });
    return { userCount, tenantCount: tenantKeys.size };
  };

  useEffect(() => {
    if (!openActionMenu) return undefined;
    const onPointerDown = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        const trigger = e.target?.closest?.("[data-action-menu-trigger]");
        if (trigger?.getAttribute("data-action-menu-trigger") === openActionMenu.id) {
          return;
        }
        setOpenActionMenu(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenActionMenu(null);
    };
    const onRepositionClose = () => setOpenActionMenu(null);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onRepositionClose);
    window.addEventListener("scroll", onRepositionClose, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onRepositionClose);
      window.removeEventListener("scroll", onRepositionClose, true);
    };
  }, [openActionMenu]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  
  const getSortedTenants = (rows) => {
    if (!sortColumn) return rows;
    const sorted = [...rows].sort((a, b) => {
      let valA, valB;
      if (sortColumn === "name") {
        valA = (a.tenantName || "").toLowerCase();
        valB = (b.tenantName || "").toLowerCase();
      } else if (sortColumn === "code") {
        valA = (a.organizationCode || "").toLowerCase();
        valB = (b.organizationCode || "").toLowerCase();
      } else if (sortColumn === "users") {
        valA = countActiveUsersForTenant(a);
        valB = countActiveUsersForTenant(b);
      }
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  };
  
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return (
      <span className="text-gray-700 ml-1">
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };
  const getDisplayTenants = () => {
    const rows = [...tenants];
    const seen = new Set(tenants.map((t) => getTenantKey(t)));

    users
      .filter(isActiveUser)
      .forEach((u) => {
        const key = u.tenantId || u.tenantName;
        if (!key || seen.has(key)) return;
        const inList = tenants.some(
          (t) => t.tenantId === u.tenantId || t.tenantName === u.tenantName,
        );
        if (!inList) {
          seen.add(key);
          rows.push({
            tenantId: u.tenantId,
            tenantName: u.tenantName,
            isActive: true,
            statistics: {
              userCount: countActiveUsersForTenant({
                tenantId: u.tenantId,
                tenantName: u.tenantName,
              }),
            },
          });
        }
      });

    return rows;
  };

  const getRoleLabel = (role) => {
    const roleDisplay = { admin: "Admin", viewer: "Read Only" };
    return (
      roleDisplay[role] ||
      (role
        ? role.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
        : "User")
    );
  };

  useEffect(() => {
    console.log("SuperAdminDashboard mounted");
    loadData();
  }, []);

  const loadData = async () => {
    console.log("Loading dashboard data...");
    try {
      setError(null);
      setLoading(true);

      let tenantsData = [];
      let usersData = [];

      try {
        const tenantsResult = await getTenants();
        console.log("Tenants result:", tenantsResult);
        if (tenantsResult?.success && tenantsResult?.data?.tenants) {
          tenantsData = tenantsResult.data.tenants;
        }
      } catch (tenantErr) {
        console.error("Failed to load tenants:", tenantErr);
      }

      try {
        const usersResult = await getAllUsers();
        console.log("Users result:", usersResult);
        if (usersResult?.success && usersResult?.data?.users) {
          usersData = usersResult.data.users;
        }
      } catch (userErr) {
        console.error("Failed to load users:", userErr);
      }

      try {
        const settingsResult = await getSuperAdminSettings();
        if (settingsResult?.success && settingsResult?.data?.defaultAccessExpirationDays != null) {
          setDefaultAccessExpirationDays(settingsResult.data.defaultAccessExpirationDays);
        }
      } catch (settingsErr) {
        console.error("Failed to load super admin settings:", settingsErr);
      }

      setTenants(tenantsData);
      setUsers(usersData);
      console.log("Data loaded successfully");
    } catch (err) {
      console.error("Dashboard error:", err);
      setError(`Failed to load dashboard: ${err.message || "Unknown error"}`);
      setTenants([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (data) => {
    try {
      const result = await createTenantWithAdmin(
        data.tenantName,
        data.email,
        data.password,
        data.logo,
        "admin",
        data.firstName,
        data.lastName,
        data.attDefaultAddress
      );
      if (result.success) {
        await loadData();
        return result;
      } else {
        // Handle error response from API
        const errorMsg = result.message || "Failed to create tenant";
        throw new Error(errorMsg);
      }
    } catch (error) {
      throw error;
    }
  };

  const handleAddUserToTenant = async (data) => {
    try {
      const result = await createUser({
        tenantId: data.tenantId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        role: data.role || "viewer",
      });
      if (result.success) {
        await loadData();
        setSuccessMessage("User added successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        return;
      }
      throw new Error(result.message || "Failed to add user");
    } catch (err) {
      throw err;
    }
  };

  const handleEditUser = (user) => {
    setEditUserModal(user);
    setEditFormData({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email,
      isActive: user.isActive,
    });
  };

  const closeEditUserModal = () => {
    setEditUserModal(null);
    setEditFormData({});
  };

  const handleSaveUser = async () => {
    if (!editUserModal) return;

    setActionLoading(true);
    setError(null);
    const payload = {
      firstName: String(editFormData.firstName ?? "").trim(),
      lastName: String(editFormData.lastName ?? "").trim(),
      email: editFormData.email,
      isActive: editFormData.isActive,
    };
    try {
      const result = await updateUser(editUserModal.id, payload);
      if (result.success) {
        await loadData();
        setEditUserModal(null);
        setEditFormData({});
        setSuccessMessage("User updated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(result.message || "Failed to update user");
      }
    } catch (err) {
      setError(err.message || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  const openExtendAccessModal = (user) => {
    const days = user.accessExpirationDays;
    const valid = [30, 60, 90, 120].includes(days) ? days : 90;
    setExtendAccessDays(valid);
    setExtendAccessUser(user);
  };

  const handleSaveExtendAccess = async () => {
    if (!extendAccessUser) return;
    setExtendAccessLoading(true);
    setError(null);
    try {
      const result = await updateUser(extendAccessUser.id, {
        accessExpirationDays: Number(extendAccessDays),
      });
      if (result.success) {
        await loadData();
        setExtendAccessUser(null);
        setSuccessMessage("Access period renewed from today.");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(result.message || "Failed to renew access");
      }
    } catch (err) {
      setError(err.message || "Failed to renew access");
    } finally {
      setExtendAccessLoading(false);
    }
  };

  const handleHideTenant = async (tenant) => {
    if (!tenant?.tenantId) return;
    setOpenActionMenu(null);
    setActionLoading(true);
    setError(null);
    try {
      const result = await toggleTenantStatus(tenant.tenantId, false);
      if (!result.success) {
        setError(result.message || "Failed to hide tenant");
        return;
      }
      const tenantUsers = getUsersForTenant(tenant).filter((u) => u.isActive !== false);
      await Promise.all(
        tenantUsers.map((u) => updateUser(u.id, { isActive: false })),
      );
      await loadData();
      setSuccessMessage(
        "Tenant hidden. Permanent delete available after 30 days.",
      );
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      setError(err.message || "Failed to hide tenant");
    } finally {
      setActionLoading(false);
    }
  };

  const handleShowTenant = async (tenant) => {
    if (!tenant?.tenantId) return;
    setOpenActionMenu(null);
    setActionLoading(true);
    setError(null);
    try {
      const result = await toggleTenantStatus(tenant.tenantId, true);
      if (result.success) {
        await loadData();
        setSuccessMessage("Tenant restored. Deletion schedule cleared.");
        setTimeout(() => setSuccessMessage(""), 4000);
      } else {
        setError(result.message || "Failed to show tenant");
      }
    } catch (err) {
      setError(err.message || "Failed to show tenant");
    } finally {
      setActionLoading(false);
    }
  };

  const openPurgeModal = async (tenant) => {
    if (!tenant?.tenantId) return;
    closeActionMenu();
    setPurgeModal(tenant);
    setPurgePreview(null);
    setPurgeConfirmation("");
    setPurgeAcknowledged(false);
    setPurgePreviewLoading(true);
    setError(null);
    try {
      const result = await getTenantPurgePreview(tenant.tenantId);
      if (result.success) {
        setPurgePreview(result.data);
      } else {
        setError(result.message || "Failed to load delete preview");
        setPurgeModal(null);
      }
    } catch (err) {
      setError(err.message || "Failed to load delete preview");
      setPurgeModal(null);
    } finally {
      setPurgePreviewLoading(false);
    }
  };

  const closePurgeModal = () => {
    if (purgeSubmitting) return;
    setPurgeModal(null);
    setPurgePreview(null);
    setPurgeConfirmation("");
    setPurgeAcknowledged(false);
  };

  const handlePermanentDeleteTenant = async () => {
    if (!purgeModal?.tenantId || !purgePreview?.canPurge) return;
    const expected =
      String(purgePreview.organizationCode || purgeModal.organizationCode || "").trim() ||
      String(purgePreview.tenantName || purgeModal.tenantName || "").trim();
    if (
      !expected ||
      purgeConfirmation.trim().toLowerCase() !== expected.toLowerCase()
    ) {
      setError(
        expected
          ? `Type ${expected} to confirm permanent delete`
          : "Confirmation text does not match",
      );
      return;
    }
    if (!purgeAcknowledged) {
      setError("Acknowledge permanent data loss to continue");
      return;
    }

    setPurgeSubmitting(true);
    setError(null);
    try {
      const result = await permanentlyDeleteTenant(purgeModal.tenantId, {
        confirmation: purgeConfirmation.trim(),
        acknowledgeDataLoss: true,
      });
      if (result.success) {
        setPurgeModal(null);
        setPurgePreview(null);
        setPurgeConfirmation("");
        setPurgeAcknowledged(false);
        await loadData();
        setSuccessMessage(
          result.message ||
            `${purgeModal.tenantName || "Tenant"} permanently deleted.`,
        );
        setTimeout(() => setSuccessMessage(""), 4000);
      } else {
        setError(result.message || "Failed to permanently delete tenant");
      }
    } catch (err) {
      setError(err.message || "Failed to permanently delete tenant");
    } finally {
      setPurgeSubmitting(false);
    }
  };

  const handleDisableUser = async (user) => {
    if (!user?.id) return;
    setOpenActionMenu(null);
    setActionLoading(true);
    setError(null);
    try {
      const result = await updateUser(user.id, { isActive: false });
      if (result.success) {
        await loadData();
        setSuccessMessage("User set to inactive.");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(result.message || "Failed to disable user");
      }
    } catch (err) {
      setError(err.message || "Failed to disable user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnableUser = async (user) => {
    if (!user?.id) return;
    setOpenActionMenu(null);
    setActionLoading(true);
    setError(null);
    try {
      const result = await updateUser(user.id, { isActive: true });
      if (result.success) {
        await loadData();
        setSuccessMessage("User set to active.");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(result.message || "Failed to enable user");
      }
    } catch (err) {
      setError(err.message || "Failed to enable user");
    } finally {
      setActionLoading(false);
    }
  };

  const openAddUserForTenant = (tenant) => {
    closeActionMenu();
    setAddUserInitialTenantId(tenant?.tenantId ? String(tenant.tenantId) : "");
    setIsAddUserModalOpen(true);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setAccessFilter("all");
  };

  /** Click Expired / Expiring summary cards to filter tenants and show those users. */
  const applyAccessCardFilter = (filter) => {
    const next = accessFilter === filter ? "all" : filter;
    setAccessFilter(next);
    setTenantVisibilityTab("all");
    setSearchQuery("");
    if (next === "all") return;

    const match = getDisplayTenants().find((tenant) => {
      const summary = getTenantAccessSummary(tenant);
      return summary.kind === next;
    });
    if (match) {
      setExpandedTenantKey(getTenantKey(match));
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserModal) return;

    setActionLoading(true);
    setError(null);
    try {
      const result = await deleteUser(deleteUserModal.id);
      if (result.success) {
        await loadData();
        setDeleteUserModal(null);
        setSuccessMessage("User deleted successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(result.message || "Failed to delete user");
        setDeleteUserModal(null);
      }
    } catch (error) {
      setError(error.message || "Failed to delete user");      setDeleteUserModal(null);
    } finally {
      setActionLoading(false);
    }
  };

  const openEmailConfigModal = (tenant) => {
    if (!tenant?.tenantId) return;
    const invoiceFrom =
      tenant.emailFrom ||
      tenant.emailReplyTo ||
      INVOICE_EMAIL_ADDRESS_FALLBACK;
    setEmailConfigModal(tenant.tenantId);
    setEmailConfigForm({
      emailDisplayName: tenant.emailDisplayName || "",
      emailFrom: invoiceFrom,
      emailReplyTo: invoiceFrom,
      emailTo: tenant.emailTo || "",
      emailCc: tenant.emailCc || "",
      emailSubject: tenant.emailSubject || "",
      customField1Name: tenant.customField1Name || "",
      customField2Name: tenant.customField2Name || "",
      invoiceNoteAutoEnabled: Boolean(tenant.invoiceNoteAutoEnabled),
      invoiceMrcChangeThreshold:
        tenant.invoiceMrcChangeThreshold != null
          ? tenant.invoiceMrcChangeThreshold
          : 5,
      invoiceClaudePrompt: tenant.invoiceClaudePrompt || "",
      monthSummaryClaudePrompt: tenant.monthSummaryClaudePrompt || "",
      monthSummaryAutoOnClose: Boolean(tenant.monthSummaryAutoOnClose),
      invoiceGrammarClaudePrompt: tenant.invoiceGrammarClaudePrompt || "",
      monthSummaryGrammarClaudePrompt:
        tenant.monthSummaryGrammarClaudePrompt || "",
      invoiceClaudeModel: tenant.invoiceClaudeModel || "",
      invoiceGrammarClaudeModel: tenant.invoiceGrammarClaudeModel || "",
      monthSummaryClaudeModel: tenant.monthSummaryClaudeModel || "",
      monthSummaryGrammarClaudeModel:
        tenant.monthSummaryGrammarClaudeModel || "",
      invoiceExtractionClaudePrompt:
        tenant.invoiceExtractionClaudePrompt || "",
      invoiceExtractionClaudeModel:
        tenant.invoiceExtractionClaudeModel || "",
    });
    const addr = tenant?.settings?.attDefaultAddress;
    setInvoiceAttStreet(addr?.street_address ?? DEFAULT_ATT_IMPORT_ADDRESS.street_address);
    setInvoiceAttCity(addr?.city ?? DEFAULT_ATT_IMPORT_ADDRESS.city);
    setInvoiceAttState(addr?.state ?? DEFAULT_ATT_IMPORT_ADDRESS.state);
    setInvoiceAttZip(addr?.zip ?? DEFAULT_ATT_IMPORT_ADDRESS.zip);
    setInvoiceAttCountry(addr?.country ?? DEFAULT_ATT_IMPORT_ADDRESS.country);
    setNitelBillingAccount(tenant?.settings?.nitelBillingAccount ?? "");
    const classifications = tenant?.settings?.siteClassifications;
    setSiteClassificationsForm(
      Array.isArray(classifications) && classifications.length > 0
        ? classifications.map((c) => String(c ?? ""))
        : [...DEFAULT_SITE_CLASSIFICATIONS],
    );
    setInvoiceLogoDataUrl(null);
    setInvoiceLogoError("");
    setSettingsError(null);
    // Reset the settings modal UI state so it always opens on a clean tab.
    setSettingsTab("general");
    setShowMonthSummaryPrompts(false);
    setShowInvoiceNotePrompts(false);
    setShowExtractionPrompts(false);
    setTenantPromptStatuses({});
    setTenantPromptMasters({});
    setPromptDiffModal(null);
    getTenantAiPromptStatuses(tenant.tenantId)
      .then((res) => {
        if (res?.success) {
          setTenantPromptStatuses(res.data?.statuses || {});
          setTenantPromptMasters(res.data?.masters || {});
        }
      })
      .catch(() => {
        /* Statuses are informational; settings still work without them. */
      });
    setBillingProvidersForm([]);
    setBillingAccountsForm([]);
    setBillingAccountsLoaded(false);
    setBillingAccountsLoading(true);
    setGlCodesForm([]);
    setGlCodesLoaded(false);
    setGlCodesLoading(true);
    getTenantBillingAccounts(tenant.tenantId)
      .then((res) => {
        if (!res?.success) {
          throw new Error(res?.message || "Could not load billing accounts");
        }
        setBillingProvidersForm(res.data?.providers || []);
        setBillingAccountsForm(res.data?.accounts || []);
        setBillingAccountsLoaded(true);
      })
      .catch((err) => {
        setSettingsError(err?.message || "Could not load billing accounts");
      })
      .finally(() => setBillingAccountsLoading(false));
    getTenantGlCodes(tenant.tenantId)
      .then((res) => {
        if (!res?.success) {
          throw new Error(res?.message || "Could not load GL codes");
        }
        setGlCodesForm(res.data?.codes || []);
        setGlCodesLoaded(true);
      })
      .catch((err) => {
        setSettingsError((prev) => prev || err?.message || "Could not load GL codes");
      })
      .finally(() => setGlCodesLoading(false));

    // Load Anthropic model list for extraction dropdown (non-blocking).
    setClaudeModelsError("");
    setClaudeModelsLoading(true);
    getClaudeModels()
      .then((res) => {
        if (res?.success && Array.isArray(res?.data?.models)) {
          setClaudeModels(res.data.models);
        } else {
          setClaudeModels([]);
          setClaudeModelsError(res?.message || "Could not load Claude models");
        }
      })
      .catch((err) => {
        setClaudeModels([]);
        setClaudeModelsError(err?.message || "Could not load Claude models");
      })
      .finally(() => setClaudeModelsLoading(false));
  };

  const closeEmailConfigModal = () => {
    setEmailConfigModal(null);
    setSettingsError(null);
    setInvoiceLogoDataUrl(null);
    setInvoiceLogoError("");
    setInvoiceAttStreet(DEFAULT_ATT_IMPORT_ADDRESS.street_address);
    setInvoiceAttCity(DEFAULT_ATT_IMPORT_ADDRESS.city);
    setInvoiceAttState(DEFAULT_ATT_IMPORT_ADDRESS.state);
    setInvoiceAttZip(DEFAULT_ATT_IMPORT_ADDRESS.zip);
    setInvoiceAttCountry(DEFAULT_ATT_IMPORT_ADDRESS.country);
    setNitelBillingAccount("");
    setSiteClassificationsForm([...DEFAULT_SITE_CLASSIFICATIONS]);
    setBillingProvidersForm([]);
    setBillingAccountsForm([]);
    setBillingAccountsLoaded(false);
    setBillingAccountsLoading(false);
    setGlCodesForm([]);
    setGlCodesLoaded(false);
    setGlCodesLoading(false);
    setTenantPromptStatuses({});
    setTenantPromptMasters({});
    setPromptDiffModal(null);
    if (invoiceLogoInputRef.current) {
      invoiceLogoInputRef.current.value = "";
    }
  };

  const resetPromptFieldToMaster = (promptKey, field) => {
    const master = tenantPromptMasters[promptKey];
    const text = master?.text ?? "";
    const version = master?.version;
    setEmailConfigForm((f) => ({ ...f, [field]: text }));
    setTenantPromptStatuses((prev) => ({
      ...prev,
      [promptKey]: {
        ...(prev[promptKey] || {}),
        status: text === "" ? "not_set" : "matches",
        statusLabel:
          text === ""
            ? "Not set"
            : version
              ? `Matches v${version}`
              : "Matches",
        matchesCurrent: text !== "" || text === (master?.text ?? ""),
      },
    }));
  };

  const handleInvoiceLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setInvoiceLogoDataUrl(null);
      setInvoiceLogoError("");
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setInvoiceLogoError("Please choose an image file (PNG, JPEG, GIF, or WebP).");
      setInvoiceLogoDataUrl(null);
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setInvoiceLogoError("Logo must be 10 MB or smaller.");
      setInvoiceLogoDataUrl(null);
      return;
    }
    setInvoiceLogoError("");
    const reader = new FileReader();
    reader.onload = () => setInvoiceLogoDataUrl(reader.result);
    reader.onerror = () => {
      setInvoiceLogoError("Failed to read the image.");
      setInvoiceLogoDataUrl(null);
    };
    reader.readAsDataURL(file);
  };

  const clearInvoiceLogo = () => {
    setInvoiceLogoDataUrl(null);
    setInvoiceLogoError("");
    if (invoiceLogoInputRef.current) {
      invoiceLogoInputRef.current.value = "";
    }
  };

  const handleSaveEmailConfig = async () => {
    if (!emailConfigModal) return;
    const cleanedClassifications = [];
    const seenClassifications = new Set();
    for (const item of siteClassificationsForm) {
      const name = String(item ?? "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seenClassifications.has(key)) continue;
      seenClassifications.add(key);
      cleanedClassifications.push(name);
    }
    if (cleanedClassifications.length === 0) {
      setSettingsError("Add at least one site classification name.");
      return;
    }
    if (billingAccountsLoaded) {
      const duplicate = findDuplicateBillingAccount(billingAccountsForm);
      if (duplicate) {
        setSettingsTab("billing-accounts");
        setSettingsError(
          `Duplicate billing account ${duplicate.billingAccount} for ${duplicate.provider}`,
        );
        return;
      }
    }
    try {
      setEmailConfigSaving(true);
      setSettingsError(null);
      setInvoiceLogoError("");
      if (billingAccountsLoaded) {
        const billingResult = await updateTenantBillingAccounts(emailConfigModal, {
          providers: billingProvidersForm,
          accounts: billingAccountsForm.map((account) => ({
            ...(account.id != null ? { id: account.id } : {}),
            provider: account.provider,
            billing_account: account.billing_account,
            display_name: account.display_name,
            is_active: account.is_active !== false,
          })),
        });
        if (!billingResult?.success) {
          throw new Error(billingResult?.message || "Failed to save billing accounts");
        }
      }
      if (glCodesLoaded) {
        const glResult = await updateTenantGlCodes(emailConfigModal, {
          codes: glCodesForm.map((code) => ({
            ...(code.id != null ? { id: code.id } : {}),
            gl_code: code.gl_code,
            description: code.description,
            is_active: code.is_active !== false,
          })),
        });
        if (!glResult?.success) {
          throw new Error(glResult?.message || "Failed to save GL codes");
        }
        setGlCodesForm(glResult.data?.codes || glCodesForm);
      }
      const result = await updateTenantInvoiceEmailSettings(
        emailConfigModal,
        {
          ...emailConfigForm,
          emailFrom: emailConfigForm.emailFrom || INVOICE_EMAIL_ADDRESS_FALLBACK,
        },
      );
      if (result.success) {
        if (invoiceLogoDataUrl) {
          const logoResult = await updateTenantLogo(emailConfigModal, invoiceLogoDataUrl);
          if (!logoResult.success) {
            setInvoiceLogoError(logoResult.message || "Settings saved but failed to update company logo.");
          } else {
            await refreshProfile();
          }
        }
        const attResult = await updateTenantAttDefaultAddress(emailConfigModal, {
          street_address: invoiceAttStreet,
          city: invoiceAttCity,
          state: invoiceAttState,
          zip: invoiceAttZip,
          country: invoiceAttCountry,
        });
        if (!attResult.success) {
          setSettingsError(
            (prev) => prev || attResult.message || "Settings saved but failed to update HQ address.",
          );
        }
        const nitelAccountResult = await updateTenantNitelBillingAccount(
          emailConfigModal,
          nitelBillingAccount,
        );
        if (!nitelAccountResult.success) {
          setSettingsError(
            (prev) =>
              prev ||
              nitelAccountResult.message ||
              "Settings saved but failed to update the Nitel billing account.",
          );
        }
        const classResult = await updateTenantSiteClassifications(
          emailConfigModal,
          cleanedClassifications,
        );
        if (!classResult.success) {
          setSettingsError(
            (prev) =>
              prev ||
              classResult.message ||
              "Settings saved but failed to update site classifications.",
          );
        }
        setTenants((prev) =>
          prev.map((t) =>
            t.tenantId === emailConfigModal
              ? {
                  ...t,
                  emailDisplayName: emailConfigForm.emailDisplayName,
                  emailFrom: emailConfigForm.emailFrom,
                  emailReplyTo: emailConfigForm.emailReplyTo,
                  emailTo: emailConfigForm.emailTo,
                  emailCc: emailConfigForm.emailCc,
                  emailSubject: emailConfigForm.emailSubject,
                  customField1Name: emailConfigForm.customField1Name,
                  customField2Name: emailConfigForm.customField2Name,
                  invoiceNoteAutoEnabled: emailConfigForm.invoiceNoteAutoEnabled,
                  invoiceMrcChangeThreshold: emailConfigForm.invoiceMrcChangeThreshold,
                  invoiceClaudePrompt: emailConfigForm.invoiceClaudePrompt,
                  monthSummaryClaudePrompt: emailConfigForm.monthSummaryClaudePrompt,
                  monthSummaryAutoOnClose: emailConfigForm.monthSummaryAutoOnClose,
                  invoiceGrammarClaudePrompt:
                    emailConfigForm.invoiceGrammarClaudePrompt,
                  monthSummaryGrammarClaudePrompt:
                    emailConfigForm.monthSummaryGrammarClaudePrompt,
                  invoiceClaudeModel: emailConfigForm.invoiceClaudeModel,
                  invoiceGrammarClaudeModel:
                    emailConfigForm.invoiceGrammarClaudeModel,
                  monthSummaryClaudeModel:
                    emailConfigForm.monthSummaryClaudeModel,
                  monthSummaryGrammarClaudeModel:
                    emailConfigForm.monthSummaryGrammarClaudeModel,
                  invoiceExtractionClaudePrompt:
                    emailConfigForm.invoiceExtractionClaudePrompt,
                  invoiceExtractionClaudeModel:
                    emailConfigForm.invoiceExtractionClaudeModel,
                }
              : t,
          ),
        );
        await loadData();
        setSuccessMessage("Tenant settings saved.");
        closeEmailConfigModal();
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setSettingsError(result.message || "Failed to save email settings");
      }
    } catch (err) {
      const message = err.message || "Failed to save email settings";
      if (/duplicate billing account/i.test(message)) {
        setSettingsTab("billing-accounts");
      }
      setSettingsError(message);
    } finally {
      setEmailConfigSaving(false);
    }
  };

  const handleGetAPIKey = async (tenantId) => {
    try {
      setApiKeyLoading(true);
      setError(null);
      // Set modal first so it shows even if API fails
      setApiKeyModal(tenantId);
      const tenant = tenants.find(t => t.tenantId === tenantId);
      setApiKeyData({ tenant_name: tenant?.tenantName || "Unknown" });
      
      const result = await getTenantPowerBIKey(tenantId);
      if (result.success) {
        setApiKeyData(result.data);
        // Show API key if it exists
        if (result.data?.api_key) {
          setShowApiKey(true);
        } else {
          setShowApiKey(false);
        }
      } else {
        setError(result.message || "Failed to get API key");
      }
    } catch (err) {
      setError(err.message || "Failed to get API key");
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleGenerateAPIKey = async (tenantId) => {
    try {
      setApiKeyLoading(true);
      setError(null);
      const result = await generatePowerBIKey(tenantId);
      if (result.success) {
        setApiKeyData(result.data);
        setApiKeyModal(tenantId);
        setShowApiKey(true); // Show API key immediately when generated
        setSuccessMessage("API key generated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(result.message || "Failed to generate API key");
      }
    } catch (err) {
      setError(err.message || "Failed to generate API key");
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRegenerateAPIKey = async (tenantId) => {
    if (!window.confirm("Are you sure you want to regenerate the API key? The old key will be invalidated.")) {
      return;
    }
    try {
      setApiKeyLoading(true);
      setError(null);
      const result = await regeneratePowerBIKey(tenantId);
      if (result.success) {
        setApiKeyData(result.data);
        setApiKeyModal(tenantId);
        setShowApiKey(true); // Show API key immediately when regenerated
        setSuccessMessage("API key regenerated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(result.message || "Failed to regenerate API key");
      }
    } catch (err) {
      setError(err.message || "Failed to regenerate API key");
    } finally {
      setApiKeyLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccessMessage("API key copied to clipboard!");
      setTimeout(() => setSuccessMessage(""), 3000);
    }).catch(() => {
      setError("Failed to copy to clipboard");
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ground,#f5f4f1)] flex items-center justify-center">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--accent,#a51c1c)]"></div>
          <span className="text-sm text-gray-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const allTenants = getDisplayTenants();
  const activeTenantCount = allTenants.filter((t) => t.isActive !== false).length;
  const hiddenTenantCount = allTenants.filter((t) => t.isActive === false).length;

  const searchLower = searchQuery.trim().toLowerCase();
  const filteredTenants = getSortedTenants(
    allTenants.filter((tenant) => {
      if (tenantVisibilityTab === "active" && tenant.isActive === false) return false;
      if (tenantVisibilityTab === "hidden" && tenant.isActive !== false) return false;

      const access = getTenantAccessSummary(tenant);
      // Keep card / ACCESS dropdown / row badge in sync: expired wins over expiring.
      if (accessFilter === "expired" && access.kind !== "expired") return false;
      if (accessFilter === "expiring" && access.kind !== "expiring") return false;
      if (accessFilter === "current" && access.kind !== "current") return false;

      if (!searchLower) return true;
      const name = String(tenant.tenantName || "").toLowerCase();
      const code = String(tenant.organizationCode || "").toLowerCase();
      const userMatch = getUsersForTenant(tenant).some((u) =>
        String(u.email || "").toLowerCase().includes(searchLower),
      );
      return name.includes(searchLower) || code.includes(searchLower) || userMatch;
    }),
  );

  const displayTenants = filteredTenants;
  const expiredSummary = getExpiredUsersSummary();
  const expiringSummary = getExpiringUsersSummary();

  const updateDefaultAccessDays = async (days) => {
    setDefaultAccessExpirationDays(days);
    try {
      await updateSuperAdminSettings({ defaultAccessExpirationDays: days });
      setSuccessMessage("Default expiration updated.");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to update default expiration");
    }
  };

  return (
    <div className="w-full px-5 py-5 pb-14">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
            Super Admin
          </p>
          <h1 className="text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)] mt-0.5">
            Tenants
          </h1>
          <p className="text-sm text-[var(--muted,#6b6a66)] mt-1">
            Every client organization on AssetLedger, its users and their access.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="inline-flex h-[38px] items-center justify-center gap-1.5 px-4 text-sm font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--accent,#a51c1c)]"
        >
          + Create tenant
        </button>
      </div>

      {successMessage ? (
        <Toast
          message={successMessage}
          type="success"
          duration={4000}
          onClose={() => setSuccessMessage("")}
        />
      ) : null}

      {error && !emailConfigModal ? (
        <Toast
          message={error}
          type="error"
          duration={8000}
          onClose={() => setError(null)}
        />
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <button
          type="button"
          onClick={() => applyAccessCardFilter("expired")}
          aria-pressed={accessFilter === "expired"}
          className={`text-left bg-white rounded-[10px] border px-5 py-4 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#D93025]/30 ${
            accessFilter === "expired"
              ? "border-[#D93025] ring-2 ring-[#D93025]/20"
              : "border-[var(--line,#e4e2dc)]"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#D93025]" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#D93025]">
              Expired users
            </p>
          </div>
          <p className="mt-1 text-[32px] font-semibold leading-none text-[#D93025]">
            {expiredSummary.userCount}
          </p>
          <p className="mt-2 text-sm text-[var(--muted,#6b6a66)]">
            Across {expiredSummary.tenantCount} tenant
            {expiredSummary.tenantCount === 1 ? "" : "s"} · renew or disable.
          </p>
        </button>
        <button
          type="button"
          onClick={() => applyAccessCardFilter("expiring")}
          aria-pressed={accessFilter === "expiring"}
          className={`text-left bg-white rounded-[10px] border px-5 py-4 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#B06000]/30 ${
            accessFilter === "expiring"
              ? "border-[#B06000] ring-2 ring-[#B06000]/20"
              : "border-[var(--line,#e4e2dc)]"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#B06000]" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#B06000]">
              Expiring in 30 days
            </p>
          </div>
          <p className="mt-1 text-[32px] font-semibold leading-none text-[#B06000]">
            {expiringSummary.userCount}
          </p>
          <p className="mt-2 text-sm text-[var(--muted,#6b6a66)]">
            Across {expiringSummary.tenantCount} tenant
            {expiringSummary.tenantCount === 1 ? "" : "s"} · renew before they lose access.
          </p>
        </button>
      </div>

      <div className="flex items-end gap-5 border-b border-[var(--line,#e4e2dc)] mb-4">
        {[
          { id: "active", label: "Active", count: activeTenantCount },
          { id: "hidden", label: "Hidden", count: hiddenTenantCount },
          { id: "all", label: "All", count: allTenants.length },
        ].map((tab) => {
          const selected = tenantVisibilityTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTenantVisibilityTab(tab.id)}
              className={`pb-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                selected
                  ? "border-[var(--accent,#a51c1c)] text-[var(--accent,#a51c1c)]"
                  : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
              }`}
            >
              {tab.label}{" "}
              <span className={selected ? "text-[var(--accent,#a51c1c)]" : "text-[var(--faint,#9a9a95)]"}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-[10px] border border-[var(--line,#e4e2dc)] px-4 py-3 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)] mb-1">
              Search
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--faint,#9a9a95)]"
                aria-hidden="true"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tenant, code or user email."
                className="w-full h-[38px] pl-9 pr-3 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-sm text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/20 focus:border-[var(--accent,#a51c1c)]"
              />
            </div>
          </div>
          <div className="w-[140px]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)] mb-1">
              Access
            </label>
            <CustomSelect
              value={accessFilter}
              onChange={(e) => setAccessFilter(e.target.value)}
              className="w-full h-[38px] border border-[var(--line-strong,#d5d3cc)] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
            >
              <option value="all">All</option>
              <option value="current">All current</option>
              <option value="expiring">Expiring</option>
              <option value="expired">Expired</option>
            </CustomSelect>
          </div>
          <div className="w-[170px]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)] mb-1">
              Default access period
            </label>
            <CustomSelect
              value={defaultAccessExpirationDays}
              onChange={(e) => updateDefaultAccessDays(Number(e.target.value))}
              className="w-full h-[38px] border border-[var(--line-strong,#d5d3cc)] rounded-md px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
            >
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={120}>120 days</option>
            </CustomSelect>
          </div>
          <button
            type="button"
            onClick={handleResetFilters}
            className="h-[38px] px-3 text-sm font-medium rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
          >
            Reset
          </button>
          <p className="ml-auto text-xs text-[var(--muted,#6b6a66)] pb-2.5 whitespace-nowrap">
            Showing {displayTenants.length} of{" "}
            {tenantVisibilityTab === "active"
              ? activeTenantCount
              : tenantVisibilityTab === "hidden"
              ? hiddenTenantCount
              : allTenants.length}{" "}
            tenants
          </p>
        </div>
      </div>

      {/* Organizations table with expandable user rows */}
      <div className="bg-white rounded-[10px] border border-[var(--line,#e4e2dc)]">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full text-sm border-collapse min-w-[960px]">
            <thead>
              <tr className="bg-[var(--sunken,#eeece7)] border-b border-[var(--line,#e4e2dc)]">
                <th
                  className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)] cursor-pointer select-none"
                  onClick={() => handleSort("name")}
                >
                  Organization
                  <SortIcon column="name" />
                </th>
                <th
                  className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)] cursor-pointer select-none"
                  onClick={() => handleSort("users")}
                >
                  Users
                  <SortIcon column="users" />
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                  Access
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                  Last activity
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {displayTenants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">
                    No organizations found
                  </td>
                </tr>
              ) : (
                displayTenants.map((tenant) => {
                  const tenantKey = getTenantKey(tenant);
                  const isExpanded = expandedTenantKey === tenantKey;
                  const tenantUsers = getUsersForTenant(tenant);
                  const userCount = countActiveUsersForTenant(tenant);
                  const adminCount = countAdminsForTenant(tenant);
                  const access = getTenantAccessSummary(tenant);
                  const lastActivity = getTenantLastActivity(tenant);
                  const deletionInfo = getTenantDeletionInfo(tenant);
                  const tenantMenuId = `tenant-${tenantKey}`;
                  const isUnused = userCount === 0 || !lastActivity;

                  return (
                    <React.Fragment key={tenantKey}>
                      <tr
                        className={`border-b border-[var(--line,#e4e2dc)] ${
                          isExpanded
                            ? "bg-white"
                            : "bg-white hover:bg-[var(--sunken,#eeece7)]/50"
                        }`}
                      >
                        <td className="px-4 py-3.5 align-middle">
                          <button
                            type="button"
                            onClick={() => toggleTenantExpanded(tenantKey)}
                            className="flex items-start gap-2 text-left focus:outline-none max-w-full"
                          >
                            <ChevronRight
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 text-[var(--accent,#a51c1c)] transition-transform ${
                                isExpanded ? "rotate-90" : ""
                              }`}
                              aria-hidden="true"
                            />
                            <span className="min-w-0">
                              <span className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-[var(--ink,#1c1c1a)]">
                                  {tenant.tenantName || "N/A"}
                                </span>
                                {isUnused && (
                                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#FEF3E2] text-[#B06000]">
                                    Unused
                                  </span>
                                )}
                                {deletionInfo.phase === "waiting" && (
                                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#FEF3E2] text-[#B06000]">
                                    Deletes in {deletionInfo.daysLeft} day
                                    {deletionInfo.daysLeft === 1 ? "" : "s"}
                                  </span>
                                )}
                                {deletionInfo.phase === "ready" && (
                                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#FCE8E6] text-[#D93025]">
                                    Ready to delete
                                  </span>
                                )}
                              </span>
                              <span className="block text-xs text-[var(--muted,#6b6a66)] mt-0.5 font-mono">
                                {tenant.organizationCode || "—"}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <div className="text-sm font-semibold text-[var(--ink,#1c1c1a)]">{userCount}</div>
                          <div className="text-xs text-[var(--muted,#6b6a66)]">
                            {adminCount} admin{adminCount === 1 ? "" : "s"}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <AccessPill kind={access.kind} label={access.label} />
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          {lastActivity ? (
                            <>
                              <div className="text-sm font-semibold text-[var(--ink,#1c1c1a)]">
                                {formatRelativePast(lastActivity)}
                              </div>
                              <div className="text-xs text-[var(--muted,#6b6a66)]">
                                Last sign in {formatDisplayDate(lastActivity)}
                              </div>
                            </>
                          ) : (
                            <span className="text-sm text-[var(--muted,#6b6a66)]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <StatusPill active={tenant.isActive !== false} />
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <div className="flex items-center justify-end gap-2">
                            {tenant.tenantId ? (
                              <button
                                type="button"
                                onClick={() => openEmailConfigModal(tenant)}
                                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                                title="Configure invoice email and notes automation settings"
                              >
                                <Settings className="w-3.5 h-3.5" aria-hidden="true" />
                                Configure
                              </button>
                            ) : null}
                            <button
                              type="button"
                              data-action-menu-trigger={tenantMenuId}
                              onClick={(e) =>
                                toggleActionMenu(
                                  { id: tenantMenuId, kind: "tenant", tenant },
                                  e,
                                )
                              }
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                              aria-label="More tenant actions"
                              aria-expanded={openActionMenu?.id === tenantMenuId}
                            >
                              <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="border-b border-[var(--line,#e4e2dc)] bg-[#FEF9F8]">
                          <td colSpan={6} className="p-0">
                            <div className="px-4 py-3 pl-10">
                              <div className="bg-white rounded-lg border border-[var(--line,#e4e2dc)] overflow-visible">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-[#F7F5F2] border-b border-[var(--line,#e4e2dc)]">
                                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                        User
                                      </th>
                                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                        Role
                                      </th>
                                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                        Status
                                      </th>
                                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                        Access expires
                                      </th>
                                      <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                        Last sign in
                                      </th>
                                      <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-[var(--line,#e4e2dc)] bg-white">
                                    {tenantUsers.length === 0 ? (
                                      <tr>
                                        <td
                                          colSpan={6}
                                          className="px-4 py-4 text-center text-gray-500 text-sm"
                                        >
                                          No users in this organization
                                        </td>
                                      </tr>
                                    ) : (
                                      tenantUsers.map((user, index) => {
                                        const safeUser = user || {};
                                        const roleText = getRoleLabel(safeUser.role);
                                        const userMenuId = `user-${safeUser.id || `${tenantKey}-${index}`}`;
                                        const fullName = [safeUser.firstName, safeUser.lastName]
                                          .filter(Boolean)
                                          .join(" ");

                                        return (
                                          <tr
                                            key={safeUser.id || `${tenantKey}-user-${index}`}
                                            className="bg-white hover:bg-[#FAFAF8]"
                                          >
                                            <td className="px-3 py-2.5 align-middle">
                                              <div className="text-sm font-semibold text-[var(--ink,#1c1c1a)]">
                                                {safeUser.email || "N/A"}
                                              </div>
                                              {fullName ? (
                                                <div className="text-xs text-[var(--muted,#6b6a66)]">
                                                  {fullName}
                                                </div>
                                              ) : null}
                                            </td>
                                            <td className="px-3 py-2.5 align-middle">
                                              <span
                                                className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                  safeUser.role === "admin"
                                                    ? "bg-[#E8F0FE] text-[#1967D2]"
                                                    : safeUser.role === "viewer"
                                                    ? "bg-[#F1F3F4] text-[#5F6368]"
                                                    : "bg-gray-100 text-gray-700"
                                                }`}
                                              >
                                                {roleText}
                                              </span>
                                            </td>
                                            <td className="px-3 py-2.5 align-middle">
                                              <StatusPill active={safeUser.isActive !== false} />
                                            </td>
                                            <td className="px-3 py-2.5 align-middle">
                                              <div className="text-sm font-semibold text-[var(--ink,#1c1c1a)]">
                                                {formatDisplayDate(safeUser.accessExpiresAt)}
                                              </div>
                                              {safeUser.accessExpiresAt ? (
                                                <div className="text-xs text-[var(--muted,#6b6a66)]">
                                                  {formatRelativeFuture(safeUser.accessExpiresAt)}
                                                </div>
                                              ) : null}
                                            </td>
                                            <td className="px-3 py-2.5 align-middle">
                                              {safeUser.lastLogin ? (
                                                <>
                                                  <div className="text-sm font-semibold text-[var(--ink,#1c1c1a)]">
                                                    {formatRelativePast(safeUser.lastLogin)}
                                                  </div>
                                                  <div className="text-xs text-[var(--muted,#6b6a66)]">
                                                    {formatDisplayDate(safeUser.lastLogin)}
                                                  </div>
                                                </>
                                              ) : (
                                                <span className="text-sm text-[var(--muted,#6b6a66)]">—</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2.5 align-middle">
                                              <div className="flex items-center justify-end gap-1.5">
                                                <button
                                                  type="button"
                                                  onClick={() => openExtendAccessModal(safeUser)}
                                                  className="inline-flex items-center justify-center h-8 px-2.5 text-xs font-medium rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                                                  title="Set access period from today"
                                                >
                                                  Renew
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => handleEditUser(safeUser)}
                                                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                                                  title="Edit user"
                                                >
                                                  <Pencil className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
                                                </button>
                                                <button
                                                  type="button"
                                                  data-action-menu-trigger={userMenuId}
                                                  onClick={(e) =>
                                                    toggleActionMenu(
                                                      {
                                                        id: userMenuId,
                                                        kind: "user",
                                                        tenant,
                                                        user: safeUser,
                                                      },
                                                      e,
                                                    )
                                                  }
                                                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                                                  aria-label="More user actions"
                                                  aria-expanded={openActionMenu?.id === userMenuId}
                                                >
                                                  <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })
                                    )}
                                  </tbody>
                                </table>
                              </div>
                              <div className="flex items-center justify-between gap-3 flex-wrap pt-3">
                                <button
                                  type="button"
                                  onClick={() => openAddUserForTenant(tenant)}
                                  className="inline-flex items-center gap-1.5 h-8 px-3 text-sm font-medium rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-white/90 shadow-sm"
                                >
                                  + Add user to {tenant.tenantName || "tenant"}
                                </button>
                                <p className="text-xs text-[var(--muted,#6b6a66)]">
                                  Renew extends access {defaultAccessExpirationDays} days from today
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openActionMenu &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={actionMenuRef}
            role="menu"
            style={{
              position: "fixed",
              top: openActionMenu.placeAbove ? undefined : openActionMenu.top,
              bottom: openActionMenu.placeAbove
                ? window.innerHeight - openActionMenu.top
                : undefined,
              right: openActionMenu.right,
              zIndex: 10000,
            }}
            className={`bg-white rounded-lg border border-[var(--line,#e4e2dc)] shadow-lg py-1 ${
              openActionMenu.kind === "tenant" ? "w-60" : "w-56"
            }`}
          >
            {openActionMenu.kind === "tenant" && openActionMenu.tenant ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => openAddUserForTenant(openActionMenu.tenant)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                >
                  <UserPlus className="w-4 h-4 text-[var(--muted,#6b6a66)]" />
                  Add user
                </button>
                {openActionMenu.tenant.tenantId ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      closeActionMenu();
                      handleGetAPIKey(openActionMenu.tenant.tenantId);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                  >
                    <KeyRound className="w-4 h-4 text-[var(--muted,#6b6a66)]" />
                    API key
                  </button>
                ) : null}
                {openActionMenu.tenant.isActive === false ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleShowTenant(openActionMenu.tenant)}
                    disabled={actionLoading}
                    className="w-full flex items-start gap-2.5 px-3 py-2 text-sm text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
                  >
                    <Eye className="w-4 h-4 mt-0.5 text-[var(--muted,#6b6a66)]" />
                    <span className="text-left">
                      <span className="block font-medium">Show tenant</span>
                      <span className="block text-xs text-[var(--muted,#6b6a66)]">
                        Restore access, cancel deletion schedule
                      </span>
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleHideTenant(openActionMenu.tenant)}
                    disabled={actionLoading || !openActionMenu.tenant.tenantId}
                    className="w-full flex items-start gap-2.5 px-3 py-2 text-sm text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
                  >
                    <EyeOff className="w-4 h-4 mt-0.5 text-[var(--muted,#6b6a66)]" />
                    <span className="text-left">
                      <span className="block font-medium">Hide tenant</span>
                      <span className="block text-xs text-[var(--muted,#6b6a66)]">
                        Blocks sign in · 30-day wait before permanent delete
                      </span>
                    </span>
                  </button>
                )}
                {(() => {
                  const info = getTenantDeletionInfo(openActionMenu.tenant);
                  if (openActionMenu.tenant.isActive !== false) {
                    return (
                      <button
                        type="button"
                        role="menuitem"
                        disabled
                        className="w-full flex items-start gap-2.5 px-3 py-2 text-sm text-[var(--faint,#9a9a95)] cursor-not-allowed"
                        title="Hide the tenant first"
                      >
                        <Trash2 className="w-4 h-4 mt-0.5" />
                        <span className="text-left">
                          <span className="block font-medium">Delete permanently</span>
                          <span className="block text-xs">Hide the tenant first</span>
                        </span>
                      </button>
                    );
                  }
                  if (!info.canPurge) {
                    const availableDate = formatDisplayDate(info.eligibleAt);
                    return (
                      <button
                        type="button"
                        role="menuitem"
                        disabled
                        className="w-full flex items-start gap-2.5 px-3 py-2 text-sm text-[var(--faint,#9a9a95)] cursor-not-allowed"
                        title={`Available ${availableDate}`}
                      >
                        <Trash2 className="w-4 h-4 mt-0.5" />
                        <span className="text-left">
                          <span className="block font-medium">Delete permanently</span>
                          <span className="block text-xs">
                            Available {availableDate}
                          </span>
                        </span>
                      </button>
                    );
                  }
                  return (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => openPurgeModal(openActionMenu.tenant)}
                      disabled={actionLoading || purgeSubmitting}
                      className="w-full flex items-start gap-2.5 px-3 py-2 text-sm text-[#D93025] hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4 mt-0.5" />
                      <span className="text-left">
                        <span className="block font-medium">Delete permanently</span>
                        <span className="block text-xs text-[#D93025]/80">
                          Removes inventory, invoices, and users
                        </span>
                      </span>
                    </button>
                  );
                })()}
              </>
            ) : null}
            {openActionMenu.kind === "user" && openActionMenu.user ? (
              <>
                {openActionMenu.user.isActive === false ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleEnableUser(openActionMenu.user)}
                    disabled={actionLoading}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
                  >
                    <CircleSlash className="w-4 h-4 text-[var(--muted,#6b6a66)]" />
                    Enable user
                  </button>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleDisableUser(openActionMenu.user)}
                    disabled={actionLoading}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
                  >
                    <Ban className="w-4 h-4 text-[var(--muted,#6b6a66)]" />
                    Disable user
                  </button>
                )}
                <div className="my-1 border-t border-[var(--line,#e4e2dc)]" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const userToDelete = openActionMenu.user;
                    closeActionMenu();
                    setDeleteUserModal(userToDelete);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#D93025] hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove from {openActionMenu.tenant?.tenantName || "tenant"}
                </button>
              </>
            ) : null}
          </div>,
          document.body,
        )}

      {/* Permanent delete confirmation (after 30-day wait) */}
      {purgeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--line,#e4e2dc)]">
              <h2 className="text-lg font-semibold text-[var(--ink,#1c1c1a)]">
                Permanently delete {purgeModal.tenantName || "tenant"}?
              </h2>
              <p className="text-sm text-[var(--muted,#6b6a66)] mt-1">
                This cannot be undone. Inventory, invoices, users, and related
                files will be removed.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {purgePreviewLoading ? (
                <p className="text-sm text-[var(--muted,#6b6a66)]">
                  Loading delete preview…
                </p>
              ) : purgePreview ? (
                <>
                  {!purgePreview.canPurge && (
                    <div className="rounded-md border border-[#FCE8E6] bg-[#FCE8E6]/60 px-3 py-2 text-sm text-[#D93025]">
                      {(purgePreview.blockers && purgePreview.blockers[0]) ||
                        "Permanent delete is not available yet."}
                    </div>
                  )}
                  <ul className="text-sm text-[var(--ink,#1c1c1a)] space-y-1.5">
                    <li>
                      <span className="font-semibold">
                        {purgePreview.counts?.users ?? 0}
                      </span>{" "}
                      users
                    </li>
                    <li>
                      <span className="font-semibold">
                        {(purgePreview.counts?.inventory ?? 0).toLocaleString()}
                      </span>{" "}
                      inventory records
                    </li>
                    <li>
                      <span className="font-semibold">
                        {purgePreview.counts?.invoices ?? 0}
                      </span>{" "}
                      invoices
                      {(purgePreview.counts?.invoiceBlobs ?? 0) > 0
                        ? ` (${purgePreview.counts.invoiceBlobs} PDF files)`
                        : ""}
                    </li>
                    <li>
                      <span className="font-semibold">
                        {purgePreview.counts?.addresses ?? 0}
                      </span>{" "}
                      addresses
                    </li>
                    <li>
                      <span className="font-semibold">
                        {purgePreview.counts?.snapshots ?? 0}
                      </span>{" "}
                      inventory snapshots
                    </li>
                  </ul>
                  {purgePreview.hiddenAt && (
                    <p className="text-xs text-[var(--muted,#6b6a66)]">
                      Hidden since {formatDisplayDate(purgePreview.hiddenAt)}
                      {purgePreview.deletionEligibleAt
                        ? ` · eligible ${formatDisplayDate(purgePreview.deletionEligibleAt)}`
                        : ""}
                    </p>
                  )}
                  <label className="flex items-start gap-2 text-sm text-[var(--ink,#1c1c1a)]">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={purgeAcknowledged}
                      onChange={(e) => setPurgeAcknowledged(e.target.checked)}
                      disabled={!purgePreview.canPurge || purgeSubmitting}
                    />
                    <span>
                      I understand invoices and inventory will be permanently
                      deleted.
                    </span>
                  </label>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--muted,#6b6a66)] mb-1">
                      Type{" "}
                      <span className="text-[var(--ink,#1c1c1a)] normal-case tracking-normal">
                        {purgePreview.organizationCode ||
                          purgePreview.tenantName ||
                          "the tenant name"}
                      </span>{" "}
                      to confirm
                    </label>
                    <input
                      type="text"
                      value={purgeConfirmation}
                      onChange={(e) => setPurgeConfirmation(e.target.value)}
                      disabled={!purgePreview.canPurge || purgeSubmitting}
                      className="w-full h-[38px] px-3 rounded-md border border-[var(--line-strong,#d5d3cc)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/20 focus:border-[var(--accent,#a51c1c)] disabled:bg-gray-50"
                      autoComplete="off"
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#D93025]">
                  Could not load delete preview.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[var(--line,#e4e2dc)] flex justify-end gap-2">
              <button
                type="button"
                onClick={closePurgeModal}
                disabled={purgeSubmitting}
                className="h-9 px-3 text-sm font-medium rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePermanentDeleteTenant}
                disabled={
                  purgeSubmitting ||
                  purgePreviewLoading ||
                  !purgePreview?.canPurge ||
                  !purgeAcknowledged ||
                  !purgeConfirmation.trim()
                }
                className="h-9 px-3 text-sm font-medium rounded-md bg-[#D93025] text-white hover:bg-[#b3261e] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purgeSubmitting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Tenant Modal (new organization + first Admin user) */}
      <CreateTenantAdminModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateTenant}
      />

      {/* Add User to Existing Tenant Modal */}
      <AddUserToTenantModal
        isOpen={isAddUserModalOpen}
        onClose={() => {
          setIsAddUserModalOpen(false);
          setAddUserInitialTenantId("");
        }}
        onSubmit={handleAddUserToTenant}
        tenants={tenants}
        initialTenantId={addUserInitialTenantId}
      />

      {/* Edit User Modal */}
      {editUserModal &&
        (() => {
          return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[999] p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
                  <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
                  <button
                    type="button"
                    onClick={closeEditUserModal}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-gray-300"
                    disabled={actionLoading}
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="flex flex-col flex-1 min-h-0">
                  <div className="overflow-y-auto flex-1 px-8">
                    <FormSection
                      isFirst
                      title="Organization"
                      description="The tenant this user belongs to."
                    >
                      <div>
                        <label className={LABEL_CLASS}>Organization</label>
                        <input
                          type="text"
                          value={editUserModal.tenantName || "N/A"}
                          className={INPUT_CLASS}
                          disabled
                          readOnly
                        />
                      </div>
                    </FormSection>

                    <FormSection
                      title="User Details"
                      description="Profile and access settings for this user."
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={LABEL_CLASS}>First Name</label>
                          <input
                            type="text"
                            value={editFormData.firstName ?? ""}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                firstName: e.target.value,
                              })
                            }
                            className={INPUT_CLASS}
                            placeholder="First name"
                            disabled={actionLoading}
                            autoComplete="given-name"
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>Last Name</label>
                          <input
                            type="text"
                            value={editFormData.lastName ?? ""}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                lastName: e.target.value,
                              })
                            }
                            className={INPUT_CLASS}
                            placeholder="Last name"
                            disabled={actionLoading}
                            autoComplete="family-name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Email Address</label>
                        <input
                          type="email"
                          value={editFormData.email || ""}
                          className={INPUT_CLASS}
                          disabled
                          readOnly
                        />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Role</label>
                        <input
                          type="text"
                          value={getEditRoleDisplay(editUserModal.role)}
                          className={INPUT_CLASS}
                          disabled
                          readOnly
                        />
                        <p className="text-xs text-gray-500 mt-1.5">
                          Role cannot be changed after the user is created.
                        </p>
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Status</label>
                        <CustomSelect
                          value={editFormData.isActive ? "active" : "inactive"}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              isActive: e.target.value === "active",
                            })
                          }
                          className={INPUT_CLASS}
                          disabled={actionLoading}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </CustomSelect>
                        <p className="text-xs text-gray-500 mt-1.5">
                          Access expires {formatAccessExpires(editUserModal.accessExpiresAt)}. To renew
                          the login period from today, use{" "}
                          <span className="font-medium text-gray-700">Renew access</span> in the table.
                        </p>
                      </div>
                    </FormSection>

                  </div>

                  <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-200 flex-shrink-0 bg-white">
                    <button
                      type="button"
                      onClick={closeEditUserModal}
                      className="px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={actionLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveUser}
                      className="px-5 py-2.5 text-sm font-medium bg-[var(--accent,#a51c1c)] text-white rounded-md hover:bg-[#8c1a1a] disabled:bg-[var(--accent,#a51c1c)]/50 disabled:cursor-not-allowed transition-colors"
                      disabled={actionLoading}
                    >
                      {actionLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Renew access — separate from Edit User so saving profile/logo does not reset expiry */}
      {extendAccessUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[999] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">Renew access</h2>
              <button
                type="button"
                onClick={() => setExtendAccessUser(null)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-gray-300"
                disabled={extendAccessLoading}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-8 py-6">
              <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                Set how long this user can log in. The new period starts from{" "}
                <span className="font-medium text-gray-800">today</span>; it does not add to the
                current end date.
              </p>
              <div className="space-y-2.5 text-sm mb-5 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                <div>
                  <span className="font-medium text-gray-700">Email: </span>
                  <span className="text-gray-600">{extendAccessUser.email}</span>
                </div>
                {extendAccessUser.tenantName && (
                  <div>
                    <span className="font-medium text-gray-700">Organization: </span>
                    <span className="text-gray-600">{extendAccessUser.tenantName}</span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Current access ends: </span>
                  <span className="text-gray-600">
                    {formatAccessExpires(extendAccessUser.accessExpiresAt)}
                  </span>
                </div>
              </div>
              <label className={LABEL_CLASS}>Access period from today</label>
              <CustomSelect
                value={extendAccessDays}
                onChange={(e) => setExtendAccessDays(Number(e.target.value))}
                className={INPUT_CLASS}
                disabled={extendAccessLoading}
              >
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={120}>120 days</option>
              </CustomSelect>
            </div>
            <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-200 flex-shrink-0 bg-white">
              <button
                type="button"
                onClick={() => setExtendAccessUser(null)}
                className="px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={extendAccessLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveExtendAccess}
                className="px-5 py-2.5 text-sm font-medium bg-[var(--accent,#a51c1c)] text-white rounded-md hover:bg-[#8c1a1a] disabled:bg-[var(--accent,#a51c1c)]/50 disabled:cursor-not-allowed transition-colors"
                disabled={extendAccessLoading}
              >
                {extendAccessLoading ? "Saving..." : "Apply renewal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteUserModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-xl">
            <h2 className="text-base font-bold text-gray-900 mb-3">Delete User</h2>
            <div className="mb-4">
              <p className="text-xs text-gray-700 mb-2">
                Are you sure you want to delete user{" "}
                <strong className="text-gray-900">{deleteUserModal.email}</strong>? This action cannot be undone.
              </p>
              <div className="bg-gray-50 p-2 rounded border border-gray-200">
                <div className="text-xs">
                  <span className="font-semibold text-gray-700">Email: </span>
                  <span className="text-gray-600">{deleteUserModal.email}</span>
                </div>
                {deleteUserModal.tenantName && (
                  <div className="text-xs mt-1">
                    <span className="font-semibold text-gray-700">Organization: </span>
                    <span className="text-gray-600">{deleteUserModal.tenantName}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteUserModal(null)}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--accent,#a51c1c)] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={actionLoading}
              >
                {actionLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice email config modal (tenant settings) — tabbed layout */}
      {emailConfigModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] flex flex-col 
          overflow-hidden">
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
  <div className="flex items-center gap-3">
    <h2 className="text-xl font-semibold text-gray-800">
      Tenant settings -
    </h2>

    {(() => {
      const activeTenant = tenants.find((t) => t.tenantId === emailConfigModal);
      const activeTenantName = activeTenant?.tenantName;

      return activeTenantName ? (
        <span className="text-xl font-semibold text-gray-800">
          {activeTenantName}
        </span>
      ) : null;
    })()}
  </div>

  <button
    type="button"
    onClick={closeEmailConfigModal}
    className="text-gray-400 hover:text-gray-600 text-xl font-bold"
  >
    ×
  </button>
</div>

            <div className="px-6 pt-4 flex-shrink-0">
              <SettingsTabBar activeTab={settingsTab} onChange={setSettingsTab} />
            </div>

            <div className="px-6 pb-2 overflow-y-auto flex-1">
              {invoiceLogoError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm">
                  {invoiceLogoError}
                </div>
              )}

              {/* ---------------- General tab: HQ address, branding, site classification ---------------- */}
              {settingsTab === "general" && (
                <>
                  <div className="pb-6 mb-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">HQ Address</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Physical billing and contact address for the organization. Shared by every user of this tenant.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className={LABEL_CLASS}>Street Address</label>
                        <input
                          type="text"
                          value={invoiceAttStreet}
                          onChange={(e) => setInvoiceAttStreet(e.target.value)}
                          className={INPUT_CLASS}
                          autoComplete="street-address"
                          placeholder="Street address"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className={LABEL_CLASS}>City</label>
                          <input
                            type="text"
                            value={invoiceAttCity}
                            onChange={(e) => setInvoiceAttCity(e.target.value)}
                            className={INPUT_CLASS}
                            autoComplete="address-level2"
                            placeholder="City"
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>State</label>
                          <input
                            type="text"
                            value={invoiceAttState}
                            onChange={(e) => setInvoiceAttState(e.target.value)}
                            className={INPUT_CLASS}
                            autoComplete="address-level1"
                            placeholder="State"
                          />
                        </div>
                        <div>
                          <label className={LABEL_CLASS}>ZIP Code</label>
                          <input
                            type="text"
                            value={invoiceAttZip}
                            onChange={(e) => setInvoiceAttZip(e.target.value)}
                            className={INPUT_CLASS}
                            autoComplete="postal-code"
                            placeholder="ZIP"
                          />
                        </div>
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Country</label>
                        <input
                          type="text"
                          value={invoiceAttCountry}
                          onChange={(e) => setInvoiceAttCountry(e.target.value)}
                          className={INPUT_CLASS}
                          autoComplete="country"
                          placeholder="Country"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pb-6 mb-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Nitel Inventory
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Used only for Nitel inventory uploads when the file does not provide an
                      account number.
                    </p>
                    <div>
                      <label className={LABEL_CLASS}>Billing Account Number</label>
                      <input
                        type="text"
                        value={nitelBillingAccount}
                        onChange={(e) => setNitelBillingAccount(e.target.value)}
                        className={INPUT_CLASS}
                        placeholder="Nitel billing account number"
                        maxLength={255}
                      />
                    </div>
                  </div>

                  <div className="pb-6 mb-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Branding</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Customize the interface appearance for this tenant. Shared by every user of this tenant.
                    </p>
                    <label className={LABEL_CLASS}>Company Logo</label>
                    {(() => {
                      const invoiceTenant = tenants.find((t) => t.tenantId === emailConfigModal);
                      const invoiceDisplayLogo =
                        invoiceLogoDataUrl || invoiceTenant?.settings?.branding?.logo;
                      return (
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <input
                            ref={invoiceLogoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                            onChange={handleInvoiceLogoChange}
                            className="sr-only"
                            id="invoice-settings-logo-upload"
                          />
                          {invoiceDisplayLogo ? (
                            <div className="flex items-center gap-4">
                              <div className="w-24 h-24 border border-gray-200 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                                <img
                                  src={invoiceDisplayLogo}
                                  alt="Logo preview"
                                  className="max-h-full max-w-full object-contain p-1"
                                />
                              </div>
                              <div className="flex flex-col gap-2">
                                <button
                                  type="button"
                                  onClick={() => invoiceLogoInputRef.current?.click()}
                                  className="text-sm font-medium text-gray-700 hover:text-gray-900 text-left"
                                >
                                  Change image
                                </button>
                                {invoiceLogoDataUrl && (
                                  <button
                                    type="button"
                                    onClick={clearInvoiceLogo}
                                    className="text-sm font-medium text-[#D94436] hover:text-[#c23d30] text-left"
                                  >
                                    Remove new
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => invoiceLogoInputRef.current?.click()}
                                className="w-24 h-24 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#D94436]/20"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                  />
                                </svg>
                                <span className="text-xs font-medium">Upload</span>
                              </button>
                              <div className="text-sm text-gray-600 pt-1">
                                <p>Upload a high-resolution PNG or SVG.</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  PNG, JPEG, GIF or WebP. Max 10 MB. Optional.
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="pb-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Site Classification</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Define the classification names available when creating/editing sites for this tenant.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {siteClassificationsForm.map((name, index) => {
                        // Size the pill to its content: width is driven by a hidden
                        // measuring span, since text inputs don't auto-size.
                        const widthCh = Math.max(String(name ?? "").length, 3) + 2;
                        return (
                          <div
                            key={index}
                            className="group flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200/70 rounded-full pl-3 pr-1.5 py-1.5 transition-colors"
                          >
                            <input
                              type="text"
                              value={name}
                              onChange={(e) => {
                                const value = e.target.value;
                                setSiteClassificationsForm((prev) =>
                                  prev.map((item, i) => (i === index ? value : item)),
                                );
                              }}
                              className="bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none min-w-[3ch]"
                              style={{ width: `${widthCh}ch` }}
                              placeholder="Classification"
                              maxLength={255}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setSiteClassificationsForm((prev) =>
                                  prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
                                )
                              }
                              disabled={siteClassificationsForm.length <= 1}
                              className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-300/70 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Remove classification"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setSiteClassificationsForm((prev) => [...prev, ""])}
                        className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 border border-dashed border-gray-300 hover:border-gray-400 rounded-full pl-2.5 pr-3 py-1.5 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        Add
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ---------------- Invoicing tab: email delivery settings ---------------- */}
              {settingsTab === "invoicing" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 pb-2">
                  <div className="sm:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Invoice Email Delivery</h3>
                    <p className="text-sm text-gray-500 mb-2">
                      Controls how invoice emails are sent to this tenant's clients.
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={LABEL_CLASS}>Display Name</label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={emailConfigForm.emailDisplayName}
                      onChange={(e) =>
                        setEmailConfigForm((f) => ({
                          ...f,
                          emailDisplayName: e.target.value,
                        }))
                      }
                      placeholder="e.g. SAIA Invoice, Americold Invoice"
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>From</label>
                    <input
                      type="email"
                      className={INPUT_CLASS}
                      value={emailConfigForm.emailFrom || INVOICE_EMAIL_ADDRESS_FALLBACK}
                      disabled
                      readOnly
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Reply-To</label>
                    <input
                      type="email"
                      className={INPUT_CLASS}
                      value={emailConfigForm.emailReplyTo || emailConfigForm.emailFrom || INVOICE_EMAIL_ADDRESS_FALLBACK}
                      disabled
                      readOnly
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>To (comma-separated)</label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={emailConfigForm.emailTo}
                      onChange={(e) =>
                        setEmailConfigForm((f) => ({ ...f, emailTo: e.target.value }))
                      }
                      placeholder="accounting@client.com"
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>CC (comma-separated)</label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={emailConfigForm.emailCc}
                      onChange={(e) =>
                        setEmailConfigForm((f) => ({ ...f, emailCc: e.target.value }))
                      }
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={LABEL_CLASS}>Subject</label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={emailConfigForm.emailSubject}
                      onChange={(e) =>
                        setEmailConfigForm((f) => ({ ...f, emailSubject: e.target.value }))
                      }
                      placeholder="Leave blank for auto-generated subject"
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Custom field 1 </label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={emailConfigForm.customField1Name}
                      onChange={(e) =>
                        setEmailConfigForm((f) => ({
                          ...f,
                          customField1Name: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Custom field 2 </label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={emailConfigForm.customField2Name}
                      onChange={(e) =>
                        setEmailConfigForm((f) => ({
                          ...f,
                          customField2Name: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* ---------------- AI automation tab — Spend Projection omitted ---------------- */}
              {settingsTab === "ai" && (
                <div className="space-y-5 pb-2">
                  <div className="rounded-md border border-[#c5daf5] bg-[#e8f0fe] px-4 py-3 text-sm text-[#1a4a8a]">
                    Prompts are managed on the{" "}
                    <button
                      type="button"
                      className="font-semibold underline underline-offset-2"
                      onClick={() => {
                        closeEmailConfigModal();
                        if (typeof onNavigate === "function") onNavigate("ai-prompts");
                      }}
                    >
                      AI prompts
                    </button>{" "}
                    page and synced from there. Editing one here makes it Custom for this
                    tenant only. Every change is logged and can be reverted.
                  </div>

                  {/* Invoice Variance Analysis (per-invoice MoM compare) */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-white">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-base font-semibold text-gray-900">
                            Invoice Variance Analysis
                          </h3>
                          <div className="inline-flex items-center gap-2">
                            <FeatureToggle
                              id="iva-enabled"
                              checked={emailConfigForm.invoiceNoteAutoEnabled}
                              onChange={(next) =>
                                setEmailConfigForm((f) => ({
                                  ...f,
                                  invoiceNoteAutoEnabled: next,
                                }))
                              }
                            />
                            <span className="text-xs font-medium text-gray-600">
                              {emailConfigForm.invoiceNoteAutoEnabled ? "On" : "Off"}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 max-w-2xl">
                          Compares two consecutive telecom invoices to identify and explain
                          changes in total charges, recurring costs, credits, usage, and
                          services. Provides a clear breakdown of what drove the variance, the
                          dollar impact of each material change, and whether the financial
                          impact is ongoing or temporary. Users see Generate analysis on any
                          invoice whose MRC changed by more than the threshold.
                        </p>
                      </div>
                    </div>

                    {emailConfigForm.invoiceNoteAutoEnabled && (
                      <>
                        <div className="mt-4 flex flex-wrap items-end gap-3">
                          <div>
                            <label className={LABEL_CLASS}>MRC change threshold</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                step="0.1"
                                className="w-24 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={emailConfigForm.invoiceMrcChangeThreshold}
                                onChange={(e) =>
                                  setEmailConfigForm((f) => ({
                                    ...f,
                                    invoiceMrcChangeThreshold: e.target.value,
                                  }))
                                }
                              />
                              <span className="text-sm text-gray-600">%</span>
                              <span className="text-xs text-gray-500">Default 5%</span>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <ClaudeModelSelect
                            label="Writing model"
                            value={emailConfigForm.invoiceClaudeModel}
                            onChange={(e) =>
                              setEmailConfigForm((f) => ({
                                ...f,
                                invoiceClaudeModel: e.target.value,
                              }))
                            }
                            models={claudeModels}
                            loading={claudeModelsLoading}
                            error={claudeModelsError}
                            defaultLabel="Platform default (Opus)"
                          />
                          <ClaudeModelSelect
                            label="Proofread model"
                            value={emailConfigForm.invoiceGrammarClaudeModel}
                            onChange={(e) =>
                              setEmailConfigForm((f) => ({
                                ...f,
                                invoiceGrammarClaudeModel: e.target.value,
                              }))
                            }
                            models={claudeModels}
                            loading={claudeModelsLoading}
                            error=""
                            defaultLabel="Platform default (Sonnet)"
                          />
                        </div>

                        <FeaturePromptsRow
                          isOpen={showInvoiceNotePrompts}
                          onToggle={() => setShowInvoiceNotePrompts((v) => !v)}
                          summaryPills={
                            <>
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                                Invoice Variance Analysis
                                <PromptStatusPill
                                  status={tenantPromptStatuses.invoice_variance_analysis?.status}
                                  label={
                                    tenantPromptStatuses.invoice_variance_analysis?.statusLabel
                                  }
                                />
                              </span>
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                                Proofread
                                <PromptStatusPill
                                  status={tenantPromptStatuses.invoice_variance_proofread?.status}
                                  label={
                                    tenantPromptStatuses.invoice_variance_proofread?.statusLabel
                                  }
                                />
                              </span>
                            </>
                          }
                        >
                          {[
                            {
                              key: "invoice_variance_analysis",
                              label: "Invoice Variance Analysis prompt",
                              field: "invoiceClaudePrompt",
                              hint: "Writes the invoice-to-invoice comparison when a user clicks Generate analysis.",
                            },
                            {
                              key: "invoice_variance_proofread",
                              label: "Proofread prompt",
                              field: "invoiceGrammarClaudePrompt",
                              hint: "Optional second pass on each analysis. Leave blank to skip it.",
                            },
                          ].map((item) => {
                            const st = tenantPromptStatuses[item.key] || {};
                            const master = tenantPromptMasters[item.key];
                            const formText = emailConfigForm[item.field] || "";
                            const matches = formText === (master?.text ?? "");
                            return (
                              <div key={item.key} className="border border-gray-100 rounded-md p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">
                                      {item.label}
                                    </span>
                                    <PromptStatusPill
                                      status={st.status}
                                      label={st.statusLabel}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-[var(--accent,#a51c1c)] hover:underline"
                                      onClick={() =>
                                        setPromptDiffModal({
                                          title: item.label,
                                          before: formText,
                                          after: master?.text || "",
                                        })
                                      }
                                    >
                                      View difference
                                    </button>
                                    {!matches && (
                                      <button
                                        type="button"
                                        className="text-xs font-medium text-gray-700 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
                                        onClick={() =>
                                          resetPromptFieldToMaster(item.key, item.field)
                                        }
                                      >
                                        Reset to master
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <AutoGrowTextarea
                                  className={`${INPUT_CLASS} font-mono text-xs`}
                                  value={emailConfigForm[item.field]}
                                  onChange={(e) =>
                                    setEmailConfigForm((f) => ({
                                      ...f,
                                      [item.field]: e.target.value,
                                    }))
                                  }
                                />
                                <p className="text-xs text-gray-500 mt-1">{item.hint}</p>
                              </div>
                            );
                          })}
                        </FeaturePromptsRow>
                      </>
                    )}
                  </div>

                  {/* Monthly Variance Summary (month close) */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-white">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-base font-semibold text-gray-900">
                            Monthly Variance Summary
                          </h3>
                          <div className="inline-flex items-center gap-2">
                            <FeatureToggle
                              id="mvs-enabled"
                              checked={emailConfigForm.monthSummaryAutoOnClose}
                              onChange={(next) =>
                                setEmailConfigForm((f) => ({
                                  ...f,
                                  monthSummaryAutoOnClose: next,
                                }))
                              }
                            />
                            <span className="text-xs font-medium text-gray-600">
                              {emailConfigForm.monthSummaryAutoOnClose ? "On" : "Off"}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 max-w-2xl">
                          Consolidates individual telecom invoice variance analyses into a
                          concise, executive level monthly summary highlighting overall
                          spending changes and the most significant financial drivers. Focuses
                          on material increases, decreases, credits, adjustments, and unusual
                          activity, providing leadership with a clear understanding of what
                          changed across the enterprise and why.
                        </p>
                      </div>
                    </div>

                    {emailConfigForm.monthSummaryAutoOnClose && (
                      <>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <ClaudeModelSelect
                            label="Writing model"
                            value={emailConfigForm.monthSummaryClaudeModel}
                            onChange={(e) =>
                              setEmailConfigForm((f) => ({
                                ...f,
                                monthSummaryClaudeModel: e.target.value,
                              }))
                            }
                            models={claudeModels}
                            loading={claudeModelsLoading}
                            error={claudeModelsError}
                            defaultLabel="Platform default (Opus)"
                          />
                          <ClaudeModelSelect
                            label="Proofread model"
                            value={emailConfigForm.monthSummaryGrammarClaudeModel}
                            onChange={(e) =>
                              setEmailConfigForm((f) => ({
                                ...f,
                                monthSummaryGrammarClaudeModel: e.target.value,
                              }))
                            }
                            models={claudeModels}
                            loading={claudeModelsLoading}
                            error=""
                            defaultLabel="Platform default (Sonnet)"
                          />
                        </div>

                        <FeaturePromptsRow
                          isOpen={showMonthSummaryPrompts}
                          onToggle={() => setShowMonthSummaryPrompts((v) => !v)}
                          summaryPills={
                            <>
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                                Monthly Variance Summary
                                <PromptStatusPill
                                  status={tenantPromptStatuses.monthly_variance_summary?.status}
                                  label={
                                    tenantPromptStatuses.monthly_variance_summary?.statusLabel
                                  }
                                />
                              </span>
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                                Proofread
                                <PromptStatusPill
                                  status={
                                    tenantPromptStatuses.monthly_variance_proofread?.status
                                  }
                                  label={
                                    tenantPromptStatuses.monthly_variance_proofread?.statusLabel
                                  }
                                />
                              </span>
                            </>
                          }
                        >
                          {[
                            {
                              key: "monthly_variance_summary",
                              label: "Monthly Variance Summary prompt",
                              field: "monthSummaryClaudePrompt",
                              hint: "Used when a month is closed to write the monthly variance summary.",
                            },
                            {
                              key: "monthly_variance_proofread",
                              label: "Proofread prompt",
                              field: "monthSummaryGrammarClaudePrompt",
                              hint: "Optional second pass on the month summary. Leave blank to skip it.",
                            },
                          ].map((item) => {
                            const st = tenantPromptStatuses[item.key] || {};
                            const master = tenantPromptMasters[item.key];
                            const formText = emailConfigForm[item.field] || "";
                            const matches = formText === (master?.text ?? "");
                            return (
                              <div key={item.key} className="border border-gray-100 rounded-md p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">
                                      {item.label}
                                    </span>
                                    <PromptStatusPill
                                      status={st.status}
                                      label={st.statusLabel}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="text-xs font-medium text-[var(--accent,#a51c1c)] hover:underline"
                                      onClick={() =>
                                        setPromptDiffModal({
                                          title: item.label,
                                          before: formText,
                                          after: master?.text || "",
                                        })
                                      }
                                    >
                                      View difference
                                    </button>
                                    {!matches && (
                                      <button
                                        type="button"
                                        className="text-xs font-medium text-gray-700 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
                                        onClick={() =>
                                          resetPromptFieldToMaster(item.key, item.field)
                                        }
                                      >
                                        Reset to master
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <AutoGrowTextarea
                                  className={`${INPUT_CLASS} font-mono text-xs`}
                                  value={emailConfigForm[item.field]}
                                  onChange={(e) =>
                                    setEmailConfigForm((f) => ({
                                      ...f,
                                      [item.field]: e.target.value,
                                    }))
                                  }
                                />
                                <p className="text-xs text-gray-500 mt-1">{item.hint}</p>
                              </div>
                            );
                          })}
                        </FeaturePromptsRow>
                      </>
                    )}
                  </div>

                  {/* Invoice Data Extraction — always on */}
                  <div className="border border-gray-200 rounded-lg p-5 bg-white">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900">
                        Invoice Data Extraction
                      </h3>
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        Always on
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 max-w-2xl">
                      Used when importing invoices into the platform to extract and standardize
                      key billing information, including provider, account number, invoice date,
                      MRC, taxes, total charges, and payment due date. Handles complex billing
                      formats and consolidated accounts, validates financial accuracy, and flags
                      missing or questionable information for review.
                    </p>

                    <div className="mt-4 max-w-md">
                      <ClaudeModelSelect
                        label="Model for extraction"
                        value={emailConfigForm.invoiceExtractionClaudeModel}
                        onChange={(e) =>
                          setEmailConfigForm((f) => ({
                            ...f,
                            invoiceExtractionClaudeModel: e.target.value,
                          }))
                        }
                        models={claudeModels}
                        loading={claudeModelsLoading}
                        error={claudeModelsError}
                        defaultLabel="Platform default (Sonnet)"
                      />
                    </div>

                    <FeaturePromptsRow
                      isOpen={showExtractionPrompts}
                      onToggle={() => setShowExtractionPrompts((v) => !v)}
                      summaryPills={
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                          Extraction
                          <PromptStatusPill
                            status={tenantPromptStatuses.invoice_extraction?.status}
                            label={tenantPromptStatuses.invoice_extraction?.statusLabel}
                          />
                        </span>
                      }
                    >
                      {(() => {
                        const item = {
                          key: "invoice_extraction",
                          label: "Extraction prompt",
                          field: "invoiceExtractionClaudePrompt",
                          hint: "Instructs extraction of invoice header fields as JSON on upload.",
                        };
                        const st = tenantPromptStatuses[item.key] || {};
                        const master = tenantPromptMasters[item.key];
                        const formText = emailConfigForm[item.field] || "";
                        const matches = formText === (master?.text ?? "");
                        return (
                          <div className="border border-gray-100 rounded-md p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-900">
                                  {item.label}
                                </span>
                                <PromptStatusPill status={st.status} label={st.statusLabel} />
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="text-xs font-medium text-[var(--accent,#a51c1c)] hover:underline"
                                  onClick={() =>
                                    setPromptDiffModal({
                                      title: item.label,
                                      before: formText,
                                      after: master?.text || "",
                                    })
                                  }
                                >
                                  View difference
                                </button>
                                {!matches && (
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-gray-700 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
                                    onClick={() =>
                                      resetPromptFieldToMaster(item.key, item.field)
                                    }
                                  >
                                    Reset to master
                                  </button>
                                )}
                              </div>
                            </div>
                            <AutoGrowTextarea
                              className={`${INPUT_CLASS} font-mono text-xs`}
                              value={emailConfigForm[item.field]}
                              onChange={(e) =>
                                setEmailConfigForm((f) => ({
                                  ...f,
                                  [item.field]: e.target.value,
                                }))
                              }
                            />
                            <p className="text-xs text-gray-500 mt-1">{item.hint}</p>
                          </div>
                        );
                      })()}
                    </FeaturePromptsRow>
                  </div>
                </div>
              )}

              {settingsTab === "budget-gl-codes" && (
                glCodesLoading ? (
                  <div className="py-12 text-center text-sm text-gray-500">
                    Loading GL codes…
                  </div>
                ) : (
                  <BudgetGlCodesSettings
                    codes={glCodesForm}
                    onCodesChange={setGlCodesForm}
                    loading={emailConfigSaving}
                  />
                )
              )}

              {settingsTab === "billing-accounts" && (
                billingAccountsLoading ? (
                  <div className="py-12 text-center text-sm text-gray-500">
                    Loading billing accounts…
                  </div>
                ) : (
                  <BillingAccountsSettings
                    providers={billingProvidersForm}
                    accounts={billingAccountsForm}
                    onProvidersChange={setBillingProvidersForm}
                    onAccountsChange={(next) => {
                      setBillingAccountsForm(next);
                      setSettingsError(null);
                    }}
                    loading={emailConfigSaving}
                  />
                )
              )}

              {settingsTab === "budget-entry" && (
                billingAccountsLoading || glCodesLoading ? (
                  <div className="py-12 text-center text-sm text-gray-500">
                    Loading budget configuration…
                  </div>
                ) : (
                  <>
                    <BudgetEntryView
                      embedded
                      canEdit
                      tenantId={emailConfigModal}
                      providers={billingProvidersForm.map((item) =>
                        String(item?.provider ?? item ?? "").trim(),
                      ).filter(Boolean)}
                      glCodes={glCodesForm.filter((code) => code?.is_active !== false)}
                      userRole="Super Admin"
                      onFooterAction={setBudgetFooterAction}
                    />
                  </>
                )
              )}

              {settingsTab === "footprint-baseline" && (
                <FootprintBaselineClientAdminView
                  embedded
                  canEdit
                  tenantId={emailConfigModal}
                  userRole="Super Admin"
                />
              )}
            </div>

            {settingsError && (
              <div className="px-6 py-2 border-t border-red-100 bg-red-50 text-sm text-red-700 flex-shrink-0">
                {settingsError}
              </div>
            )}
            {!SELF_SAVE_SETTINGS_TABS.has(settingsTab) && (
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-white">
              <button
                type="button"
                onClick={closeEmailConfigModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEmailConfig}
                disabled={
                  emailConfigSaving ||
                  billingAccountsLoading ||
                  glCodesLoading ||
                  (settingsTab === "billing-accounts" && !billingAccountsLoaded) ||
                  (settingsTab === "budget-gl-codes" && !glCodesLoaded)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-[var(--accent,#a51c1c)] rounded-md hover:bg-[#8c1a1a] disabled:opacity-60"
              >
                {emailConfigSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
            )}
            {SELF_SAVE_SETTINGS_TABS.has(settingsTab) && (
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-white">
              {settingsTab === "budget-entry" && budgetFooterAction?.add && (
                <button
                  type="button"
                  onClick={budgetFooterAction.add}
                  className="mr-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {budgetFooterAction.addLabel}
                </button>
              )}
              <button
                type="button"
                onClick={closeEmailConfigModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              {settingsTab === "budget-entry" && budgetFooterAction && (
                <button
                  type="button"
                  onClick={budgetFooterAction.save}
                  disabled={budgetFooterAction.saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-[var(--accent,#a51c1c)] rounded-md hover:bg-[#8c1a1a] disabled:opacity-60"
                >
                  {budgetFooterAction.saving ? "Saving..." : budgetFooterAction.label}
                </button>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {promptDiffModal && (
        <div
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPromptDiffModal(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-gray-200"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={promptDiffModal.title}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                View difference · {promptDiffModal.title}
              </h3>
              <button
                type="button"
                onClick={() => setPromptDiffModal(null)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              <p className="text-xs text-gray-500 mb-3">
                Red = tenant text removed vs master. Green = master text added.
              </p>
              <div className="border border-gray-200 rounded-md p-3 max-h-[50vh] overflow-y-auto">
                <DiffView
                  before={promptDiffModal.before}
                  after={promptDiffModal.after}
                />
              </div>
              <div className="mt-3 flex items-center gap-4 text-[11px] text-gray-500">
                <span>
                  <span className="line-through text-red-700">removed</span>
                </span>
                <span>
                  <span className="text-green-800">added</span>
                </span>
              </div>
            </div>
            <div className="flex justify-end px-5 py-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setPromptDiffModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Key Modal */}
      {apiKeyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[999] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">API Key</h2>
              <button
                type="button"
                onClick={() => {
                  setApiKeyModal(null);
                  setApiKeyData(null);
                  setShowApiKey(false);
                  setError(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-gray-300"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-8 py-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-5 text-sm">
                  {error}
                </div>
              )}

              <div className="text-sm mb-5 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                <span className="font-medium text-gray-700">Organization: </span>
                <span className="text-gray-600">{apiKeyData?.tenant_name || "Unknown"}</span>
              </div>

              {apiKeyLoading ? (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent,#a51c1c)] mx-auto" />
                  <p className="text-sm text-gray-500 mt-3">Loading...</p>
                </div>
              ) : apiKeyData?.api_key ? (
                <div>
                  <label className={LABEL_CLASS}>API Key</label>
                  <div className="relative">
                    <textarea
                      value={apiKeyData.api_key}
                      readOnly
                      rows={3}
                      className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-md text-sm font-mono bg-gray-50 text-gray-900 resize-none overflow-auto focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
                      style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(apiKeyData.api_key)}
                      className="absolute top-2.5 right-2.5 inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-200/70 transition-colors"
                      title="Copy API Key to Clipboard"
                      aria-label="Copy API Key to Clipboard"
                    >
                      <Copy className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  {apiKeyData.warning && (
                    <div className="mt-3 bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-md text-sm">
                      {apiKeyData.warning}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {apiKeyData?.api_key_exists ? (
                    <div>
                      <label className={LABEL_CLASS}>API Key</label>
                      <div className="relative">
                        <textarea
                          value={apiKeyData.api_key || ""}
                          readOnly
                          rows={3}
                          className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-md text-sm font-mono bg-gray-50 text-gray-900 resize-none overflow-auto focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
                          style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}
                          onClick={(e) => e.target.select()}
                        />
                        <button
                          type="button"
                          onClick={() => copyToClipboard(apiKeyData.api_key || "")}
                          className="absolute top-2.5 right-2.5 inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-200/70 transition-colors"
                          title="Copy API Key to Clipboard"
                          aria-label="Copy API Key to Clipboard"
                        >
                          <Copy className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-md px-4 py-3">
                      <p className="text-sm text-gray-700">
                        No API key generated yet. Generate one to enable access.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {!apiKeyLoading && (
              <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-200 flex-shrink-0 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    setApiKeyModal(null);
                    setApiKeyData(null);
                    setShowApiKey(false);
                    setError(null);
                  }}
                  className="px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {apiKeyData?.api_key || apiKeyData?.api_key_exists ? (
                  <button
                    type="button"
                    onClick={() => handleRegenerateAPIKey(apiKeyModal)}
                    className="px-5 py-2.5 text-sm font-medium bg-[var(--accent,#a51c1c)] text-white rounded-md hover:bg-[#8c1a1a] disabled:bg-[var(--accent,#a51c1c)]/50 disabled:cursor-not-allowed transition-colors"
                    disabled={apiKeyLoading}
                  >
                    {apiKeyLoading ? "Regenerating..." : "Regenerate Key"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleGenerateAPIKey(apiKeyModal)}
                    className="px-5 py-2.5 text-sm font-medium bg-[var(--accent,#a51c1c)] text-white rounded-md hover:bg-[#8c1a1a] disabled:bg-[var(--accent,#a51c1c)]/50 disabled:cursor-not-allowed transition-colors"
                    disabled={apiKeyLoading}
                  >
                    {apiKeyLoading ? "Generating..." : "Generate Key"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;